// Layer 3 seed (static): Bunker Hill Zn-Pb-Ag concentrator, 1,800 stpd,
// startup month 4 · 08 Jun 2026. Numbers come from
// docs/meetings/inputs/2026-08-13-bunkerhill-role-dashboards-v5.pptx,
// the module-system docx (Module 1 §6–§8), and the Concentrator Process
// Flow UI Concept docx (NODE_INSIGHTS). Source map + number-conflict
// register: docs/audits/2026-08-14-module-1-corpus.md (deck = affordance
// authority, Paul docs = number authority; D1–D4 conflicts stay flagged
// in StreamSpec provenance). Live mock is this plant only
// (progressing-optiflow.A6).

import type { ControlLoopState } from '@/versions/v1/schema/advisory-issue';
import type {
  KpiTile,
  OperatorTaskPlan,
  PlantRole,
  PlantRoleId,
  RoleAlert,
  RoleTab,
  RoleTable,
} from '@/versions/v1/schema/plant-role';
import type { DiagnosticRule } from '@/versions/v1/schema/diagnostic-rule';
import type {
  IngestStage,
  SourceDocument,
  SourceKind,
} from '@/versions/v1/schema/source-document';
import type {
  RecirculatingLoop,
  Stream,
  StreamSpec,
} from '@/versions/v1/schema/stream';
import type { Plant } from '@/versions/v1/schema/plant';
import type { NodeDisplay } from '@/versions/v1/schema/node-display';
import type { ReportInstance, ReportTemplate } from '@/versions/v1/schema/report';
import type {
  NodeInsight,
  ProcessConstraint,
  SampleAdherence,
  SamplePoint,
  SampleSource,
  Subprocess,
  UnitOperation,
  UtilizationRank,
} from '@/versions/v1/schema/unit-operation';
import {
  constraintFromReading,
  specFromReading,
} from '@/versions/v1/shared/reading.derive';
import {
  READINGS,
  STREAM_FLOW_READING_IDS,
  STREAM_SPEC_COPY,
  UNIT_KEY_READING_IDS,
} from '@/versions/v1/shared/bunker-hill-readings';
import { formatClock } from '@/versions/v1/shared/clock.derive';
import { activeCrewLabel } from '@/versions/v1/shared/bunker-hill-org-seed';
import { DEFAULT_CURSOR } from '@/versions/v1/schema/reading-series';
import { seriesAt, trendBundle, valueAt } from '@/versions/v1/shared/series.derive';
import { INITIAL_SESSION_STATE } from '@/versions/v1/schema/scenario';
// Single source for the arsenic smelter-contract limit (deck slide 8) — see
// review M11: the KPI subs, trend reference lines and penalty-projection
// narrative below used to duplicate `0.5` / `0.1` as literals in three more
// places on the same tab `ConcentratePenaltyPanel` already sources from the
// entity; this makes a change to the deck-sourced limit move all of them.
import { arsenicContractLimit } from '@/versions/v1/shared/bunker-hill-concentrate-specs';
import { readingKpiFields, recoveryTableFields } from './bunker-hill-seed.transform';
import { formatValue, formatBand } from './reading.derive';

const seedReading = (id: string) => {
  const reading = READINGS.find((row) => row.id === id)!;
  return { ...reading, value: valueAt(id, INITIAL_SESSION_STATE) ?? reading.value };
};
export const BM01_FEED = seedReading('rd-kpv-bm-feed');
const BM01_FEED_LABEL = formatValue(BM01_FEED, { withUnit: true });
const BM01_FEED_DESIGN = formatValue(BM01_FEED, { value: BM01_FEED.design, withUnit: true });
const BM01_FEED_BAND = formatBand(BM01_FEED.band!, BM01_FEED.unit, { withUnit: true });
const AG_RECOVERY_TABLE = recoveryTableFields(seedReading('rd-kpi-ag-rec'));
const AG_RECOVERY_SHIFTS = seriesAt('rs-ag-rec-shift', INITIAL_SESSION_STATE).points.slice(-2);

const ARSENIC_CONTRACT_PB = arsenicContractLimit('Pb').toFixed(2);
const ARSENIC_CONTRACT_ZN = arsenicContractLimit('Zn').toFixed(2);

/** Derived from `rd-kpi-pb-rec` at seed init — kept in sync with `pbRecoveryTile` / `pbRecoveryTableCells`. */
function derivePbRecoverySeed() {
  const reading = READINGS.find((row) => row.id === 'rd-kpi-pb-rec');
  if (!reading) throw new Error('rd-kpi-pb-rec missing from READINGS');
  const actual = reading.value ?? 85.1;
  const design = reading.design ?? 88.2;
  const budget = reading.target!;
  const ptDelta = actual - design;
  const ptSign = ptDelta >= 0 ? '+' : '−';
  const vsBudgetPct = ((actual - budget) / budget) * 100;
  const vsDesignPct = ((actual - design) / design) * 100;
  return {
    tile: {
      value: `${actual.toFixed(1)}%`,
      sub: `Design ${design.toFixed(1)} · budget ${budget.toFixed(1)} · ${ptSign}${Math.abs(ptDelta).toFixed(1)} pt vs design`,
      tone: 'bad' as const,
    },
    table: {
      value: actual.toFixed(1),
      deltaVsTarget: `${vsBudgetPct >= 0 ? '+' : ''}${vsBudgetPct.toFixed(1)}%`,
      deltaVsDesign: `${vsDesignPct >= 0 ? '+' : ''}${vsDesignPct.toFixed(1)}%`,
    },
  };
}

export const PB_RECOVERY_SEED = derivePbRecoverySeed();
export const PB_RECOVERY_TILE = PB_RECOVERY_SEED.tile;
export const PB_RECOVERY_TABLE = PB_RECOVERY_SEED.table;

function formatRecoveryTableCell(n: number, decimals = 1): string {
  return n.toFixed(decimals);
}

function buildRecoveryTableRows(): RoleTable['rows'] {
  const bundle = trendBundle(['rs-zn-rec', 'rs-pb-rec', 'rs-ag-rec'], INITIAL_SESSION_STATE, { points: 7 });
  const znDesign = READINGS.find((r) => r.id === 'rd-kpi-zn-rec')?.design ?? 90;
  const pbDesign = READINGS.find((r) => r.id === 'rd-kpi-pb-rec')?.design ?? 87;
  const agDesign = READINGS.find((r) => r.id === 'rd-kpi-ag-rec')?.design ?? 94;
  const rows: { id: string; cells: string[] }[] = [
    {
      id: 'tr-1',
      cells: [
        'Design',
        formatRecoveryTableCell(znDesign),
        formatRecoveryTableCell(pbDesign),
        formatRecoveryTableCell(agDesign),
      ],
    },
  ];
  for (let i = 0; i < bundle.categories.length; i++) {
    rows.push({
      id: `tr-${i + 2}`,
      cells: [
        bundle.categories[i]!,
        formatRecoveryTableCell(bundle.series[0]!.points[i]!),
        formatRecoveryTableCell(bundle.series[1]!.points[i]!),
        formatRecoveryTableCell(bundle.series[2]!.points[i]!),
      ],
    });
  }
  return rows;
}

/** B: Shift Compliance header — active crew at the session cursor. */
export function bunkerHillCrewLabel(cursor = DEFAULT_CURSOR): string {
  return activeCrewLabel(cursor);
}

/** @deprecated Use `bunkerHillCrewLabel(cursor)` or `activeCrewLabel(cursor)`. */
export const BUNKER_HILL_CREW = bunkerHillCrewLabel();

/** B: title · S1 design throughput. Phase month drives chrome badges and GM kicker. */
export const PLANT: Plant = {
  id: 'bunker-hill',
  name: 'Lead-zinc concentrator',
  product: 'Zn-Pb-Ag',
  designThroughput: '1,800 stpd',
  phase: 'startup',
  phaseMonth: 4,
  mineDispatch: {
    vendor: 'komatsu',
    blendApi: true,
    locationRateApi: true,
    offPairZoneSharePct: 60,
    provenance:
      '[syn] Site variant — Komatsu dispatch integration (blend + location rate APIs), Komatsu pre-brief 2026-09-11',
  },
  seniorMetallurgist: 'absorbed-by-manager',
};

/**
 * Word-doc Module 1 §4 reconstruction narration, one line per stage.
 * Counts are corpus-verified (C1 §2/§4/§8). FMEA + flowsheet are seeded so
 * the Record is total over SourceKind; setup cards do not surface them.
 */
export const INGEST_STAGE_SCRIPTS: Record<SourceKind, readonly IngestStage[]> = {
  'dcs-fd': [
    { id: 'fd-read', label: 'Reading 173 sections', name: 'Reading sections' },
    { id: 'fd-skeleton', label: 'Subprocess skeleton from heading hierarchy (7)', name: 'Subprocess skeleton from heading hierarchy' },
    { id: 'fd-appendix', label: 'Appendix B tables' },
    { id: 'fd-constraints', label: '10 constraint records', name: 'Constraint records' },
    { id: 'fd-sampling', label: 'Sampling philosophy' },
    { id: 'fd-sa', label: 'SA01–SA07' }, // SA-F25 sweep: left as-is by design -- this stage narrates only what the FD document itself contributed (SA-F24 decision: SA08 is `paul-call.A4` #253-sourced, not FD-sourced), so it agrees with the FD Details card and `cl-samples-fd` rather than the plant-wide 8-point register
  ],
  'io-list': [
    { id: 'io-read', label: 'Reading 1,263 rows', name: 'Reading rows' },
    { id: 'io-join', label: 'Joining equipment tags to the model' },
    { id: 'io-pid', label: 'PID# references retained for P&ID resolution' },
  ],
  'mass-balance': [
    { id: 'mb-formula', label: 'Walking formula references (F4 = A4 + P19)' },
    // polish.P18 (#705, adjudication 3): the edge count is STREAMS's, the same
    // figure the mass-balance and P&ID details read — never typed. A getter,
    // because STREAMS is declared further down this module.
    {
      id: 'mb-edges',
      name: 'Stream edges, merges and splits',
      get label() {
        return `${STREAMS.length} stream edges, merges and splits`;
      },
    },
    { id: 'mb-loops', label: '6 recirculating loops (248% circulating load)', name: 'Recirculating loops' },
  ],
  pid: [
    { id: 'pid-read', label: 'Reading drawing set' },
    { id: 'pid-resolve', label: 'Resolving PID-32N-xxx references from the I/O list' },
    {
      id: 'pid-validate',
      name: 'Validating inferred topology',
      get label() {
        return `Validating inferred topology (${STREAMS.length} edges)`;
      },
    },
    { id: 'pid-correct', label: 'Corrections applied' },
    { id: 'pid-auth', label: 'Connectivity now authoritative' },
  ],
  flowsheet: [
    { id: 'pfd-read', label: 'Reading process flow diagram' },
    { id: 'pfd-sequence', label: 'Area sequence crusher through paste' },
    { id: 'pfd-backbone', label: 'Model backbone approved' },
  ],
  fmea: [
    { id: 'fmea-read', label: 'Reading 13 circuit sheets', name: 'Reading circuit sheets' },
    { id: 'fmea-signatures', label: 'Failure signatures and monitoring variables' },
    { id: 'fmea-rules', label: 'Met Diagnostics IF → THEN rules' },
  ],
  // paul-fmea.A7 (#274): a non-plant technical paper — narration stays
  // honest that this is literature, not a plant artefact; it yields a
  // proposed rule claim, never a live one, on ingest alone.
  paper: [
    { id: 'paper-read', label: 'Reading Smith, Bruckard & Sparrow (2022)' },
    { id: 'paper-extract', label: 'Extracting depression mechanisms for Sb/Bi sulfosalts' },
    { id: 'paper-propose', label: 'Proposed rule drafted — routed to FMEA review, not auto-approved' },
  ],
  'control-plan': [
    { id: 'cp-read', label: 'Reading Zn circuit control plan' },
    { id: 'cp-tags', label: 'Tag-map rows with reliability and cadence' },
    { id: 'cp-spec', label: 'Spec overlays on monitored variables (conformance only)' },
  ],
  tarp: [
    { id: 'tarp-read', label: 'Reading symptom-level TARP ladders' },
    { id: 'tarp-ladders', label: 'Tiered actions attached to rule claims' },
    { id: 'tarp-drift', label: 'Drift handling — same workflow as other source kinds' },
  ],
};

export const SOURCE_DOCUMENTS: readonly SourceDocument[] = [
  {
    id: 'src-dcs-fd',
    kind: 'dcs-fd',
    title: 'DCS functional description',
    summary:
      'Process/control hierarchy: Plant → Area → Equipment → Control loop → Tag. Canonical structure every other document hangs off.',
    artifact: '173 sections',
    status: 'ingested',
  },
  {
    id: 'src-io',
    kind: 'io-list',
    title: 'I/O list',
    summary:
      'Site-specific tag-mapping table: PID reference, equipment tag, I/O type, range, manufacturer.',
    artifact: '1,263 signal rows',
    status: 'ingested',
  },
  {
    id: 'src-fmea',
    kind: 'fmea',
    title: 'FMEA workbook',
    summary:
      'Failure-knowledge layer with monitoring variables, early-warning signatures, and Met Diagnostics IF → THEN rules.',
    artifact: '13 circuit sheets',
    status: 'ingested',
  },
  {
    id: 'src-balance',
    kind: 'mass-balance',
    title: 'Flowsheet / mass-water balance',
    summary:
      'Static process model: streams, equipment list, reagent addition points — ground truth the failure logic refers back to.',
    artifact: 'Design 1,800 stpd',
    status: 'ingested',
  },
  {
    id: 'src-pid',
    kind: 'pid',
    title: 'P&ID drawings',
    summary:
      'Piping and instrumentation — not yet folded into the model. Ingest to attach remaining loops to unit operations.',
    artifact: 'Plant drawing set',
    status: 'pending',
  },
  {
    id: 'src-pfd',
    kind: 'flowsheet',
    title: 'Process flow diagram',
    summary:
      'Area sequence from crusher through paste backfill. Approved as the model backbone.',
    artifact: 'Single-line PFD',
    status: 'approved',
  },
  {
    id: 'src-paper-sb-bi',
    kind: 'paper',
    title: 'Rejection of antimony and bismuth in sulphide flotation — a literature review',
    summary:
      'Smith, Bruckard & Sparrow (2022), Mineral Processing and Extractive Metallurgy 131(3), DOI 10.1080/25726641.2021.1967079 — CSIRO literature review on pH/pulp-potential depression of Sb (stibnite, tetrahedrite, jamesonite) and Bi (bismuthinite) sulfosalts. Ingested for the penalty-element gap in Paul’s Met Diagnostics register (MD-054); yields one proposed rule, not a live one — a paper is not plant ground truth.',
    artifact: '1 literature review, pp. 260–270',
    status: 'ingested',
  },
  {
    id: 'src-control-plan-zn',
    kind: 'control-plan',
    title: 'Control Plan — Zn flotation circuit',
    summary:
      'Site control plan for the Zn circuit: tag-map rows with spec overlays and signal reliability. Conformance overlays only — never tier triggers.',
    artifact: '5 monitored rows',
    status: 'ingested',
  },
  {
    id: 'src-tarp-zn',
    kind: 'tarp',
    title: 'TARP — Zn recovery symptom ladders',
    summary:
      'Symptom-level tiered action sets for CuSO₄ activation and Zn rougher pH — attached to the rule claims they name.',
    artifact: '3 ladders · 11 tiers',
    status: 'ingested',
  },
];

export const UNIT_OPERATIONS: readonly UnitOperation[] = [
  {
    id: 'CR01',
    type: 'crusher',
    name: 'Surface crusher',
    area: 'Crushing',
    keyReadingIds: UNIT_KEY_READING_IDS.CR01,
    sourceIds: ['src-dcs-fd', 'src-balance', 'src-pfd'],
  },
  {
    id: 'BM01',
    type: 'ball_mill',
    name: 'Ball mill (primary)',
    area: 'Grinding',
    keyReadingIds: UNIT_KEY_READING_IDS.BM01,
    sourceIds: ['src-dcs-fd', 'src-io', 'src-balance'],
  },
  {
    id: 'CY01',
    type: 'cyclone',
    name: 'Cyclone cluster',
    area: 'Classification',
    keyReadingIds: UNIT_KEY_READING_IDS.CY01,
    sourceIds: ['src-dcs-fd', 'src-io', 'src-balance'],
  },
  {
    id: 'RG01',
    type: 'regrind',
    name: 'Regrind mill',
    area: 'Grinding',
    keyReadingIds: UNIT_KEY_READING_IDS.RG01,
    sourceIds: ['src-dcs-fd', 'src-balance'],
  },
  {
    id: 'PB01',
    type: 'flotation',
    name: 'Pb flotation',
    area: 'Pb circuit',
    keyReadingIds: UNIT_KEY_READING_IDS.PB01,
    sourceIds: ['src-dcs-fd', 'src-fmea', 'src-balance'],
  },
  {
    id: 'ZN01',
    type: 'flotation',
    name: 'Zn flotation',
    area: 'Zn circuit',
    keyReadingIds: UNIT_KEY_READING_IDS.ZN01,
    sourceIds: ['src-dcs-fd', 'src-fmea', 'src-balance'],
  },
  {
    id: 'TK-ZN',
    type: 'thickener',
    name: 'Zn thickener',
    area: 'Dewatering',
    keyReadingIds: UNIT_KEY_READING_IDS['TK-ZN'],
    sourceIds: ['src-dcs-fd', 'src-balance'],
  },
  {
    id: 'FL-ZN',
    type: 'filter',
    name: 'Zn filter',
    area: 'Dewatering',
    keyReadingIds: UNIT_KEY_READING_IDS['FL-ZN'],
    sourceIds: ['src-dcs-fd', 'src-balance'],
  },
  {
    id: 'PS01',
    type: 'paste',
    name: 'Paste / tails',
    area: 'Tailings',
    keyReadingIds: UNIT_KEY_READING_IDS.PS01,
    sourceIds: ['src-dcs-fd', 'src-balance', 'src-pfd'],
  },
];


export const PLANT_ROLES: readonly PlantRole[] = [
  {
    id: 'gm',
    label: 'GM',
    kicker: `Strategic overview · ${PLANT.phase} month ${PLANT.phaseMonth}`,
  },
  { id: 'superintendent', label: 'Superintendent', kicker: 'Plant-wide performance' },
  { id: 'metallurgist', label: 'Control metallurgist', kicker: 'Shift window · cases · TARP tier 2' },
  {
    id: 'senior-metallurgist',
    label: 'Senior metallurgist',
    kicker: 'Rule-set stewardship · operating record · reagent trend',
  },
  { id: 'ops-supervisor', label: 'Ops supervisor', kicker: 'Shift management' },
  { id: 'maintenance', label: 'Maintenance', kicker: 'Equipment health · work orders' },
  { id: 'cro', label: 'Control room', kicker: 'DCS / live process variables' },
  { id: 'operator', label: 'Operator', kicker: 'Shift tasks · readings · safety' },
];

export const ROLE_KPIS: readonly KpiTile[] = [
  { id: 'gm-ore', roleId: 'gm', label: 'Ore processed', ...readingKpiFields(seedReading('rd-kpi-ore-td')) },
  { id: 'gm-zn', roleId: 'gm', label: 'Zn recovery', ...readingKpiFields(seedReading('rd-kpi-zn-rec')) },
  { id: 'gm-pb', roleId: 'gm', label: 'Pb recovery', value: PB_RECOVERY_TILE.value, sub: PB_RECOVERY_TILE.sub, tone: PB_RECOVERY_TILE.tone },
  { id: 'gm-ag', roleId: 'gm', label: 'Ag recovery', ...readingKpiFields(seedReading('rd-kpi-ag-rec')) },

  { id: 'sup-crush', roleId: 'superintendent', label: 'Crushed', value: '864 t', sub: '24h · target 900 · −4.0%', tone: 'warn' },
  { id: 'sup-mill', roleId: 'superintendent', label: 'Milled', ...readingKpiFields(seedReading('rd-kpi-ore-td')) },
  { id: 'sup-util', roleId: 'superintendent', label: 'Mill utilisation', ...readingKpiFields(seedReading('rd-kpi-mill-util')) },
  { id: 'sup-zn', roleId: 'superintendent', label: 'Zn recovery', ...readingKpiFields(seedReading('rd-kpi-zn-rec')) },
  { id: 'sup-pb', roleId: 'superintendent', label: 'Pb recovery', value: PB_RECOVERY_TILE.value, sub: PB_RECOVERY_TILE.sub, tone: PB_RECOVERY_TILE.tone },
  { id: 'sup-paste', roleId: 'superintendent', label: 'Paste backfill', ...readingKpiFields(seedReading('rd-unit-ps01-paste'), 't/d') },

  { id: 'met-zn-feed', roleId: 'metallurgist', label: 'Zn feed grade', ...readingKpiFields(seedReading('rd-head-zn'), '%') },
  { id: 'met-zn-conc', roleId: 'metallurgist', label: 'Zn conc grade', ...readingKpiFields(seedReading('rd-kpi-zn-grade'), '%') },
  { id: 'met-zn-rec', roleId: 'metallurgist', label: 'Zn recovery', ...readingKpiFields(seedReading('rd-kpi-zn-rec')) },
  { id: 'met-pb-feed', roleId: 'metallurgist', label: 'Pb feed grade', ...readingKpiFields(seedReading('rd-head-pb'), '%') },
  { id: 'met-pb-conc', roleId: 'metallurgist', label: 'Pb conc grade', ...readingKpiFields(seedReading('rd-kpi-pb-grade'), '%') },
  { id: 'met-ag-feed', roleId: 'metallurgist', label: 'Ag feed grade', ...readingKpiFields(seedReading('rd-head-ag')) },
  { id: 'met-ag', roleId: 'metallurgist', label: 'Ag in Pb/Ag conc', ...readingKpiFields(seedReading('rd-kpi-ag-grade')) },

  { id: 'ops-t', roleId: 'ops-supervisor', label: 'Shift tonnes', ...readingKpiFields(seedReading('rd-kpi-milled-day')) },
  { id: 'ops-h', roleId: 'ops-supervisor', label: 'Shift hours', value: '5.5 / 12 h', sub: '46% elapsed · 11:30', tone: 'ok' },
  { id: 'ops-oee', roleId: 'ops-supervisor', label: 'OEE', value: '91.4%', sub: '+1.2 pt vs last shift', tone: 'ok' },
  { id: 'ops-al', roleId: 'ops-supervisor', label: 'Active alarms', value: '4', sub: '3 alarms · 1 case', tone: 'warn' }, // C1 AG-CONC re-homed to case group — headline 4 = 3 DCS alarms + 1 case

  { id: 'mnt-wo', roleId: 'maintenance', label: 'Open work orders', value: '12', sub: '3 high · 6 med · 3 low', tone: 'warn' },
  { id: 'mnt-pm', roleId: 'maintenance', label: 'Overdue PMs', value: '1', sub: 'Regrind mill liner inspection', tone: 'bad' }, // B: slide 11 WO-435
  { id: 'mnt-mtbf', roleId: 'maintenance', label: 'MTBF (30d)', value: '210 h', sub: '+18 h vs prior', tone: 'ok' }, // [syn] A §M11 — no downtime data
  { id: 'mnt-mttr', roleId: 'maintenance', label: 'MTTR (30d)', value: '2.8 h', sub: '−0.5 h vs prior', tone: 'ok' }, // [syn] A §M11 — no downtime data

  { id: 'cro-bm', roleId: 'cro', label: 'Ball mill fresh feed', ...readingKpiFields(BM01_FEED) },
  { id: 'cro-dens', roleId: 'cro', label: 'Mill discharge dens.', ...readingKpiFields(seedReading('rd-kpv-mill-dens')) },
  { id: 'cro-znph', roleId: 'cro', label: 'Zn float pH', ...readingKpiFields(seedReading('rd-kpv-zn-ph')) },
  { id: 'cro-ag', roleId: 'cro', label: 'Ag in Pb/Ag conc', ...readingKpiFields(seedReading('rd-kpi-ag-grade')) },

  { id: 'op-ore', roleId: 'operator', label: 'Ore to mill', ...readingKpiFields(seedReading('rd-kpi-milled-day')) },
  { id: 'op-zn', roleId: 'operator', label: 'Zn recovery', ...readingKpiFields(seedReading('rd-kpi-zn-rec')), tone: 'bad' },
  { id: 'op-pb', roleId: 'operator', label: 'Pb recovery', value: PB_RECOVERY_TILE.value, sub: PB_RECOVERY_TILE.sub, tone: PB_RECOVERY_TILE.tone },
  { id: 'op-ag', roleId: 'operator', label: 'Ag in Pb conc', ...readingKpiFields(seedReading('rd-kpi-ag-grade')) }, // K20 round 1 (#717) — tone derived by readingKpiFields from the value vs design (12.14 ≥ 12.13 → ok); the hard-coded 'bad' drew a red up-arrow on an at-design figure
  { id: 'op-paste', roleId: 'operator', label: 'Paste backfill', ...readingKpiFields(seedReading('rd-unit-ps01-paste'), 't/d'), tone: 'bad' },

  // The 07 Jun day label keeps 91.4 % reading as the last complete production
  // day (D25), never as a week figure beside the crew-matrix average.
  { id: 'crew-plant', roleId: 'superintendent', tabId: 'crew', label: 'Plant compliance', value: '91.4%', sub: '07 Jun · vs 95% target · −3.6 pt', tone: 'warn' },
  { id: 'crew-sample', roleId: 'superintendent', tabId: 'crew', label: 'Sample compliance', value: '34/39', sub: 'on time', tone: 'warn' },
  { id: 'crew-rounds', roleId: 'superintendent', tabId: 'crew', label: 'Operator rounds', value: '93.2%', sub: '40/43 complete · +1.2 pt', tone: 'ok' },
  { id: 'crew-reports', roleId: 'superintendent', tabId: 'crew', label: 'Reports & records', value: '97.8%', sub: 'Shift rpts + LIMS · on target', tone: 'ok' },

  { id: 'samp-reg', roleId: 'metallurgist', tabId: 'samples', label: 'Sample register', value: '87.5%', sub: '12/16 streams on time', tone: 'warn' },
  { id: 'samp-tat', roleId: 'metallurgist', tabId: 'samples', label: 'Lab TAT (avg)', value: '2.8 h', sub: 'Target ≤ 3 h', tone: 'ok' },
  { id: 'samp-sp', roleId: 'metallurgist', tabId: 'samples', label: 'Setpoint compliance', value: '82.3%', sub: 'Zn pH dragging · target 90%', tone: 'warn' },
  { id: 'samp-reag', roleId: 'metallurgist', tabId: 'samples', label: 'Reagent supply', value: '1 CRIT', sub: 'Aero 5100 < 7 days', tone: 'bad' },

  { id: 'as-rom', roleId: 'metallurgist', tabId: 'impurities', label: 'As in ROM feed', value: '0.12%', sub: 'Ore grade as-received', tone: 'warn' },
  { id: 'as-pb', roleId: 'metallurgist', tabId: 'impurities', label: 'As in Pb/Ag conc', value: '0.32%', sub: `Smelter limit ${ARSENIC_CONTRACT_PB}% · +0.10 pt / 30d`, tone: 'warn' },
  { id: 'as-zn', roleId: 'metallurgist', tabId: 'impurities', label: 'As in Zn conc', value: '0.06%', sub: `Smelter limit ${ARSENIC_CONTRACT_ZN}%`, tone: 'ok' },
  { id: 'as-tails', roleId: 'metallurgist', tabId: 'impurities', label: 'As in final tails', value: '0.10%', sub: '~89% of As to tails', tone: 'ok' },

  { id: 'sh-comp', roleId: 'ops-supervisor', tabId: 'shift-compliance', label: 'Shift compliance', value: '88.6%', sub: 'Target 95%', tone: 'warn' },
  { id: 'sh-rec', roleId: 'ops-supervisor', tabId: 'shift-compliance', label: 'Records complete', value: '8/9', sub: '1 cal pending', tone: 'warn' },
];

/** Additional shift compliance checks backing the Records complete KPI (B: slide 10). */
export type ShiftComplianceCheck = {
  id: string;
  label: string;
  state: SampleAdherence;
};

export const SHIFT_COMPLIANCE_CHECKS: readonly ShiftComplianceCheck[] = [
  {
    id: 'sc-1',
    label: 'Pre-shift safety inspection & JSA (all operators)',
    state: 'REG',
  }, // B: slide 10
  {
    id: 'sc-2',
    label: 'pH meter calibration (Pb circuit) — solution check ok',
    state: 'REG',
  }, // B: slide 10
  {
    id: 'sc-3',
    label: 'pH meter calibration (Zn circuit) — due 12:00',
    state: 'DUE',
  }, // B: slide 10 · 1 cal pending
  {
    id: 'sc-4',
    label: 'Densitometer zero/span check — Pb + Zn',
    state: 'REG',
  }, // B: slide 10
  {
    id: 'sc-5',
    label: 'Reagent dosing pump calibration — all 6 reagents',
    state: 'REG',
  }, // B: slide 10
  {
    id: 'sc-6',
    label: 'Housekeeping inspection log — not submitted yet',
    state: 'PENDING',
  }, // B: slide 10 · LIMS? on deck
  {
    id: 'sc-7',
    label: 'Shift handover report — due end of shift',
    state: 'UPCOMING',
  }, // B: slide 10 · — on deck
  {
    id: 'sc-8',
    label: 'Environmental containment check — spill bunds',
    state: 'REG',
  }, // B: slide 10
  {
    id: 'sc-9',
    label: 'Shift operating log — DCS entries reviewed',
    state: 'REG',
  }, // B: slide 10 · ninth record backing 8/9 KPI [syn]
];

export type EquipmentHealthIndexRow = {
  id: string;
  asset: string;
  healthPct: number;
  band: { warn: number; bad: number };
};

/** Equipment health index — health % and light bands for maintenance / GM reads. */
export const EQUIPMENT_HEALTH_INDEX: readonly EquipmentHealthIndexRow[] = [
  { id: 'eh-1', asset: 'Ball mill (primary)', healthPct: 82, band: { warn: 80, bad: 65 } },
  { id: 'eh-2', asset: 'Regrind mill', healthPct: 90, band: { warn: 80, bad: 65 } },
  { id: 'eh-3', asset: 'Pb flotation', healthPct: 91, band: { warn: 80, bad: 65 } },
  { id: 'eh-4', asset: 'Pb recleaner', healthPct: 62, band: { warn: 80, bad: 65 } },
  { id: 'eh-5', asset: 'Pb/Ag filter', healthPct: 55, band: { warn: 80, bad: 65 } },
  { id: 'eh-6', asset: 'Zn flotation', healthPct: 90, band: { warn: 80, bad: 65 } },
  { id: 'eh-7', asset: 'Zn thickener', healthPct: 87, band: { warn: 80, bad: 65 } },
  { id: 'eh-8', asset: 'Paste/tails plant', healthPct: 93, band: { warn: 80, bad: 65 } },
];

export const ROLE_TABLES: readonly RoleTable[] = [
  {
    roleId: 'gm',
    tabId: 'overview',
    caption: 'Startup performance · design vs budget target vs actual (month 4)',
    columns: ['KPI', 'Design', 'Budget target', 'Actual (24h)', 'Δ vs target', 'Δ vs Design'],
    rows: [
      { id: 'gm-r1', cells: ['Ore processed (t/d)', '1,800', '1,440', '1,720', '+19.4%', '−4.4%'] },
      { id: 'gm-r2', cells: ['Zn recovery (%)', '85.1', '69.8', '82.3', '+17.9%', '−3.3%'] },
      {
        id: 'gm-r3',
        cells: [
          'Pb recovery (%)',
          '88.2',
          '75.0',
          PB_RECOVERY_TABLE.value,
          PB_RECOVERY_TABLE.deltaVsTarget,
          PB_RECOVERY_TABLE.deltaVsDesign,
        ],
      },
      { id: 'gm-r4', cells: ['Ag recovery (%)', AG_RECOVERY_TABLE.design, AG_RECOVERY_TABLE.target, AG_RECOVERY_TABLE.actual, AG_RECOVERY_TABLE.deltaTarget, AG_RECOVERY_TABLE.deltaDesign] },
      { id: 'gm-r5', cells: ['Zn conc grade (% Zn)', '58.0', '58.0', '58.4', '+0.7%', '+0.7%'] },
      { id: 'gm-r6', cells: ['Pb conc grade (% Pb)', '67.0', '67.0', '67.1', '+0.1%', '+0.1%'] },
      { id: 'gm-r7', cells: ['Ag in Pb/Ag conc (oz/t)', '12.13', '12.13', '12.14', '+0.1%', '+0.1%'] },
      { id: 'gm-r8', cells: ['Mill utilisation (%)', '92.0', '92.0', '92.1', '+0.1%', '+0.1%'] },
    ],
  },
  {
    roleId: 'superintendent',
    tabId: 'plant-wide',
    caption: 'Production performance · day / night / 24h',
    columns: [
      'Metric',
      'Design',
      'Budget target',
      'Day shift',
      'Night shift',
      '24h actual',
      'Δ vs target',
      'Δ vs design',
    ],
    rows: [
      {
        id: 'sup-r1',
        cells: ['Mines tonnes to surface (t)', '—', '—', '—', '—', '—', 'n/a', 'n/a'],
      },
      { id: 'sup-r2', cells: ['Crushed tonnes (t)', '900', '900', '436', '428', '864', '−4.0%', '−4.0%'] },
      { id: 'sup-r3', cells: ['Milled tonnes (t)', '900', '900', '870', '850', '1,720', '−4.4%', '−4.4%'] },
      {
        id: 'sup-r4',
        cells: ['Mill utilisation (%)', '92.0', '92.0', '92.4', '91.8', '92.1', '+0.1%', '+0.1%'],
      },
      {
        id: 'sup-r5',
        cells: ['Mill availability (%)', '96.0', '96.0', '95.8', '96.2', '96.0', '0.0%', '0.0%'],
      },
      {
        id: 'sup-r6',
        cells: ['Avg mill rate (t/h)', '75.0', '75.0', '72.5', '70.8', '71.7', '−4.4%', '−4.4%'],
      },
      { id: 'sup-r7', cells: ['Zn recovery (%)', '85.1', '69.8', '82.5', '82.0', '82.3', '+17.9%', '−3.3%'] },
      {
        id: 'sup-r8',
        cells: [
          'Pb recovery (%)',
          '88.2',
          '75.0',
          '85.5',
          '84.9',
          PB_RECOVERY_TABLE.value,
          PB_RECOVERY_TABLE.deltaVsTarget,
          PB_RECOVERY_TABLE.deltaVsDesign,
        ],
      },
      { id: 'sup-r9', cells: ['Ag recovery (%)', AG_RECOVERY_TABLE.design, AG_RECOVERY_TABLE.target, AG_RECOVERY_SHIFTS[1]!.value!.toFixed(1), AG_RECOVERY_SHIFTS[0]!.value!.toFixed(1), AG_RECOVERY_TABLE.actual, AG_RECOVERY_TABLE.deltaTarget, AG_RECOVERY_TABLE.deltaDesign] },
      { id: 'sup-r10', cells: ['Zn concentrate (t)', '143.7', '94.3', '66.4', '65.5', '131.9', '+39.9%', '−8.2%'] },
      { id: 'sup-r11', cells: ['Pb/Ag concentrate (t)', '56.9', '38.7', '26.7', '25.7', '52.4', '+35.4%', '−7.9%'] },
      { id: 'sup-r12', cells: ['Ag produced (oz)', '877', '470', '302', '290', '592', '+26.0%', '−32.5%'] },
      { id: 'sup-r13', cells: ['Tails tonnes (t)', '810', '821', '820', '802', '1,622', '−0.7%', '+0.1%'] },
      { id: 'sup-r14', cells: ['Paste backfill (t)', '—', '350', '162', '156', '318', '−9.1%', 'n/a'] },
    ],
  },
  {
    roleId: 'metallurgist',
    tabId: 'recovery',
    caption: 'Circuit assay results · last shift vs design grades',
    columns: ['Stream', 'Zn %', 'Pb %', 'Ag oz/st', 'Fe %', 'S %', 'Note'],
    rows: [
      { id: 'met-r1', cells: ['ROM Feed', formatValue(seedReading('rd-head-zn')), formatValue(seedReading('rd-head-pb')), formatValue(seedReading('rd-head-ag')), '8.1', '11.8', `Ag vs ${seedReading('rd-head-ag').design!.toFixed(3)} oz/st design`] },
      {
        id: 'met-r2',
        cells: ['Cyclone O/F (BM)', '5.62', '2.48', '1.02', '8.3', '12.1', 'P80 = 82 µm (design 74)'],
      },
      {
        id: 'met-r3',
        cells: ['Regrind O/F (conc)', '4.8', '42.5', '10.8', '12.4', '18.6', 'P80 = 30 µm ✓'],
      },
      { id: 'met-r4', cells: ['Pb Rougher Conc', '7.2', '38.1', '12.4', '15.2', '19.1', 'To regrind'] },
      { id: 'met-r5', cells: ['Pb/Ag Final Conc', '1.8', '67.1', '12.14', '5.2', '21.4', 'Spec: Pb > 67%'] },
      { id: 'met-r6', cells: ['Pb Tails / Zn Feed', '6.1', '0.31', '0.06', '8.8', '12.8', 'Zn circuit feed'] },
      { id: 'met-r7', cells: ['Zn Final Conc', '58.4', '0.70', '0.08', '3.8', '29.4', 'Spec: Zn > 55% ✓'] },
      { id: 'met-r8', cells: ['Final Tails', '1.08', '0.40', '0.04', '7.0', '9.4', 'Zn, Pb over target'] },
    ],
  },
  {
    roleId: 'ops-supervisor',
    tabId: 'equipment',
    caption: 'Equipment status board',
    columns: ['Equipment', 'Status', 'Key param', 'Avail %', 'Power'],
    rows: [
      { id: 'ops-r1', cells: ['Surface Crusher', 'RUN', '680 t/h', '100', '—'] },
      { id: 'ops-r2', cells: ['Ball Mill (Primary)', 'WARN', BM01_FEED_LABEL, '96', '1,842 kW'] },
      { id: 'ops-r3', cells: ['Regrind Mill (Conc.)', 'RUN', 'P80 = 30 µm', '100', '280 kW'] },
      { id: 'ops-r4', cells: ['Pb Rougher (×4)', 'RUN', 'pH 8.4', '98', '—'] },
      { id: 'ops-r5', cells: ['Pb Cleaner (×2)', 'RUN', 'pH 8.2', '100', '—'] },
      { id: 'ops-r6', cells: ['Pb Recleaner', 'WARN', 'Air flow low', '90', '—'] },
      { id: 'ops-r7', cells: ['Zn Rougher (×6)', 'RUN', 'pH 10.8', '98', '—'] },
      { id: 'ops-r8', cells: ['Zn Cleaner (×2)', 'RUN', 'pH 10.6', '100', '—'] },
      { id: 'ops-r9', cells: ['Pb/Ag Thickener', 'RUN', '61% UF', '100', '—'] },
      { id: 'ops-r10', cells: ['Zn Thickener', 'RUN', '58% UF', '100', '—'] },
      { id: 'ops-r11', cells: ['Pb/Ag Filter', 'RUN', 'Moisture 9.4%', '98', '—'] },
      { id: 'ops-r12', cells: ['Tailings/Paste Plant', 'RUN', '318 t/d', '100', '—'] },
    ],
  },
  {
    roleId: 'maintenance',
    tabId: 'overview',
    caption: 'Open work orders · priority sorted',
    columns: ['WO', 'Asset', 'Description', 'Priority', 'Status', 'Est h', 'Assigned'],
    rows: [
      {
        id: 'mnt-r1',
        cells: ['WO-441', 'Ball Mill', 'Trunnion bearing temp – inspect/replace', 'HIGH', 'OPEN', '12', 'J. Smith'],
      },
      { id: 'mnt-r2', cells: ['WO-438', 'Pb Recleaner', 'Air sparger rebuild', 'HIGH', 'IN PRG', '6', 'K. Jones'] },
      {
        id: 'mnt-r3',
        cells: ['WO-436', 'Pb/Ag Filter', 'Filter cloth change (overdue)', 'HIGH', 'OPEN', '8', 'Unassigned'],
      },
      {
        id: 'mnt-r4',
        cells: ['WO-435', 'Regrind Mill', 'Liner inspection – PM overdue', 'MED', 'SCHD', '4', 'T. Brown'],
      },
      {
        id: 'mnt-r5',
        cells: ['WO-433', 'Tails Pump 4', 'Seal water regulator overhaul', 'MED', 'OPEN', '5', 'K. Jones'],
      },
      {
        id: 'mnt-r6',
        cells: ['WO-431', 'Zn Thickener', 'Rake drive gearbox oil service', 'MED', 'SCHD', '3', 'J. Smith'],
      },
      {
        id: 'mnt-r7',
        cells: ['WO-428', 'Cyclone cluster', 'Replace 3x worn apex liners', 'LOW', 'OPEN', '2', 'T. Brown'],
      },
      {
        // [syn] — opened before the 08 Jun demo cursor; the asset maps directly to CR01.
        id: 'mnt-r8',
        cells: ['WO-442', 'Primary Crusher', 'Crusher drive inspection — vibration follow-up', 'MED', 'OPEN', '3', 'J. Smith'],
        openedAt: '2026-06-04T06:00',
      },
      {
        // [syn] — opened before the 08 Jun demo cursor; the asset maps directly to GP01.
        id: 'mnt-r9',
        cells: ['WO-443', 'Cyclone Feed Pump', 'Pump bearing vibration route follow-up', 'LOW', 'IN PRG', '2', 'K. Jones'],
        openedAt: '2026-06-05T06:00',
      },
    ],
  },
  {
    roleId: 'cro',
    tabId: 'overview',
    caption: 'Key process variables vs setpoint',
    columns: ['Variable', 'Reading', 'Setpoint', 'Status'],
    rows: [
      { id: 'cro-r1', cells: ['Ball mill fresh feed', BM01_FEED_LABEL, `Design ${BM01_FEED_DESIGN}`, 'WARN'] },
      { id: 'cro-r2', cells: ['Mill discharge dens.', '72.1%', '70–74', 'OK'] },
      { id: 'cro-r3', cells: ['Cyclone press.', '84 kPa', '82–92', 'OK'] },
      { id: 'cro-r4', cells: ['Regrind P80', '30 µm', '27–33', 'OK'] },
      { id: 'cro-r5', cells: ['Pb float pH', '8.4', '8.2–8.6', 'OK'] },
      // B: slide 12 — the deck's own KPV table carries no status column, and its 'HIGH'
      // is the ACTIVE ALARMS severity beside ZN-pH-01, a different panel. Carried into this
      // column it read as a direction and contradicted its own row: 10.2 against 10.8–11.2 is
      // below band, and the deck's alarm text is "Zn Float pH LOW 10.2 (SP=10.8)".
      { id: 'cro-r6', cells: ['Zn float pH', '10.2', '10.8–11.2', 'LOW'] },
      { id: 'cro-r7', cells: ['ZnSO₄ dosing', '310 g/t', '300', 'OK'] },
      { id: 'cro-r8', cells: ['SMBS dosing', '195 g/t', '200', 'OK'] },
      { id: 'cro-r9', cells: ['3418A dosing', '42 g/t', '45', 'WARN'] },
      { id: 'cro-r10', cells: ['CuSO₄ dosing', '155 g/t', '150', 'OK'] },
      // B: slide 12 — the deck's row read `29.8 oz/t · Spec>25`; K20 (#717)
      // re-based the value to the balance's 12.14 oz/t, and the untraced `>25`
      // floor went with it. #730: the row reads the ConcentrateSpec's own Ag
      // content grade (`rd-spec-ag-grade`) against its design basis, and
      // `croKpvTable` derives the status (12.14 ≥ 12.13 → quiet). These cells
      // are the fallback where that derivation cannot answer.
      { id: 'cro-r11', cells: ['Ag grade Pb/Ag conc', '12.14 oz/t', 'Design 12.13 oz/t', 'OK'] },
      // B: slide 12 read `Spec >51`; polish.P15 (adjudication 7) re-points the row
      // to the ConcentrateSpec's one Zn floor (`cs-zn` › Zn grade › `rd-spec-zn-grade`,
      // > 55 % Zn) through `croKpvTable`, as #730 did for `cro-r11`. These cells are
      // the fallback where that derivation cannot answer. polish.P15 r1 (item 6): the
      // table's own percent format (no space before a `%` unit) and sentence case.
      { id: 'cro-r12', cells: ['Zn conc grade', '58.4% Zn', 'Spec >55% Zn', 'OK'] },
    ],
  },
  {
    roleId: 'operator',
    tabId: 'overview',
    caption: 'My shift task list · day 06:00–18:00',
    columns: ['Area', 'Task', 'Status'],
    rows: [
      // Slide 13's twelve checklist rows, in slide order (done → in progress → open).
      { id: 'op-r1', cells: ['SAFETY', 'Complete pre-shift safety inspection & JSA', 'Done'] },
      { id: 'op-r2', cells: ['PROCESS', `Check & record ball mill feed rate (${BM01_FEED_LABEL})`, 'Done'] },
      { id: 'op-r-pbph', cells: ['PROCESS', 'Verify Pb flotation pH — adjust lime if pH < 8.2', 'Done'] },
      { id: 'op-r-head7', cells: ['SAMPLE', 'Collect 07:00 head sample for lab', 'Done'] },
      { id: 'op-r3', cells: ['PROCESS', 'Raise Zn circuit pH to SP 11.0 — lime cell 4', 'In progress'] }, // polish.P16 r1: names the setpoint the 08:00 lime step set (`ZN_PH_SETPOINT.after`), not the old 10.8
      { id: 'op-r4', cells: ['REAGENT', 'Check 3418A dosing pump — Ag grade falling six shifts', 'In progress'] }, // K20 round 1 (#717) — the premise is the fall, not a level below design
      {
        // [syn] — walkthrough support: ZN-REC evidence request ev-smbs, not on slide 13
        id: 'op-r-smbs',
        cells: ['REAGENT', 'SMBS dosing check in the Pb circuit — is actual above design?', 'Due'],
      },
      { id: 'op-r5', cells: ['SAMPLE', 'Collect 10:00 Zn & Pb/Ag concentrate samples', 'Due'] },
      { id: 'op-r6', cells: ['LUBE', 'Grease ball mill & regrind bearings (4h freq)', 'Due'] },
      { id: 'op-r7', cells: ['INSPECT', 'Walk tailings/paste — pump 4 seal water', 'Due'] },
      { id: 'op-r-manual12', cells: ['RECORD', '12:00 manual readings — densities & levels', 'Due'] },
      { id: 'op-r-head13', cells: ['SAMPLE', 'Collect 13:00 head sample', 'Due'] },
      { id: 'op-r8', cells: ['HANDOVER', 'Prepare end-of-shift report for supervisor', 'Due'] },
    ],
  },
];

/**
 * The Operator's day-shift task plan (polish.P16 #703, comp adjudications 7
 * and 9): the area each shift task belongs to (the round sheet groups tasks by
 * area, in plant order) and its due time on the day-shift clock. A time the
 * task text names is its own; every other time is `[syn]`, placed where the
 * task falls in the shift. The links tie a task to the ZN-REC SMBS evidence
 * request it answers, the ZN-PH-01 immediate response it carries out and the
 * safety alert it works under.
 */
export const OPERATOR_TASK_PLAN: readonly OperatorTaskPlan[] = [
  { taskId: 'op-r1', area: 'shift', due: '06:00' }, // [syn] pre-shift, at the start of the shift
  { taskId: 'op-r2', area: 'sp-grind', due: '06:00' }, // [syn] first round of the shift
  { taskId: 'op-r-pbph', area: 'sp-pb', due: '06:00' }, // [syn] first round of the shift
  { taskId: 'op-r-head7', area: 'samples', due: '07:00' }, // named in the task
  { taskId: 'op-r3', area: 'sp-zn', due: '12:00', caseId: 'case-zn-ph-response', alertId: 'al-ppe' }, // [syn] due by the 12:00 round
  { taskId: 'op-r4', area: 'sp-pb', due: '14:00' }, // [syn]
  { taskId: 'op-r-smbs', area: 'sp-pb', due: '12:00', askId: 'ev-smbs' }, // [syn] answered before the 12:00 round
  { taskId: 'op-r5', area: 'samples', due: '10:00' }, // named in the task
  { taskId: 'op-r6', area: 'sp-grind', due: '14:00' }, // [syn] 4 h after the 10:00 greasing
  { taskId: 'op-r7', area: 'sp-paste', due: '16:00' }, // [syn]
  { taskId: 'op-r-manual12', area: 'shift', due: '12:00' }, // named in the task
  { taskId: 'op-r-head13', area: 'samples', due: '13:00' }, // named in the task
  { taskId: 'op-r8', area: 'shift', due: '18:00' }, // [syn] the end of the shift
];

export const ROLE_ALERTS: readonly RoleAlert[] = [
  { id: 'al-ppe', roleId: 'operator', severity: 'high', text: 'Zn pH low — full PPE near Zn cell 4', time: '06:42' },
  { id: 'al-nacn', roleId: 'operator', severity: 'med', text: 'NaCN dosing check — respiratory PPE required', time: '05:10' },
  { id: 'al-conf', roleId: 'operator', severity: 'low', text: 'Paste plant area — confined space protocol', time: '06:00' },
];

export const ROLE_TABS: readonly RoleTab[] = [
  { id: 'exec', roleId: 'gm', label: 'Executive summary' },
  { id: 'esc', roleId: 'gm', label: 'Escalated issues' },
  { id: 'perf', roleId: 'gm', label: 'Performance vs budget' },
  { id: 'shifts', roleId: 'superintendent', label: '7-day shift performance' },
  { id: 'costs', roleId: 'superintendent', label: 'MTD costs and financial' },
  { id: 'issues', roleId: 'superintendent', label: 'Issues by role' },
  { id: 'plan', roleId: 'superintendent', label: 'Production vs plan' },
  { id: 'kpi', roleId: 'metallurgist', label: 'Process KPI trends' },
  { id: 'lims', roleId: 'metallurgist', label: 'Lab data (LIMS)' },
  { id: 'cases', roleId: 'metallurgist', label: 'Active AI cases' },
  { id: 'health', roleId: 'senior-metallurgist', label: 'Process health' },
  { id: 'trends', roleId: 'senior-metallurgist', label: '7-day trend analysis' },
  { id: 'diag', roleId: 'senior-metallurgist', label: 'Diagnostic cases' },
  { id: 'kb', roleId: 'senior-metallurgist', label: 'KB update suggestions' },
  // The Maintenance landing's three view labels live in its own transform
  // (`MaintenanceLanding.transform.ts`, `maintStripCells`), so the old
  // `equip` / `wos` / `rel` seed entries are gone (polish.P14 r1 #701).
  { id: 'equipment', roleId: 'ops-supervisor', label: 'Equipment' },
  { id: 'shift-compliance', roleId: 'ops-supervisor', label: 'Shift compliance' },
  { id: 'overview', roleId: 'cro', label: 'Overview' },
  { id: 'overview', roleId: 'operator', label: 'Overview' },
];

export function roleTabsFor(roleId: PlantRoleId): readonly RoleTab[] {
  return ROLE_TABS.filter((tab) => tab.roleId === roleId);
}


export const SAMPLE_POINTS: readonly SamplePoint[] = [
  { id: 'SA01', stream: 'Grinding circuit output (cyclone O/F)', frequency: 'Every 2 h (min. 4/shift)', method: 'manual', spec: 'P80 = 74 µm', latencyClass: 'shift', adherence: 'REG' },
  { id: 'SA02', stream: 'Pb rougher feed', frequency: 'Every 4 h (min. 2/shift)', method: 'manual', spec: 'Feeds Pb recovery & grade accounting', latencyClass: 'shift', adherence: 'REG' },
  { id: 'SA03', stream: 'Pb/Ag final concentrate', frequency: 'Every 2 h (min. 4/shift)', method: 'manual', spec: '67.0% Pb, 12.13 oz/ton Ag (design-basis)', latencyClass: 'rush', adherence: 'REG' },
  { id: 'SA04', stream: 'Zn final concentrate', frequency: 'Every 2 h (min. 4/shift)', method: 'manual', spec: '58.0% Zn, Pb contamination < 3%', latencyClass: 'rush', adherence: 'REG' },
  { id: 'SA05', stream: 'Final tailings', frequency: 'Every 2 h (min. 4/shift)', method: 'manual', spec: 'Pb < 0.3%, Zn < 0.5%', latencyClass: 'shift', adherence: 'LATE' },
  { id: 'SA06', stream: 'Pb rougher concentrate', frequency: 'Every 4 h (min. 2/shift)', method: 'manual', spec: 'Feeds Pb recovery & grade accounting', latencyClass: 'shift', adherence: 'MISS' },
  { id: 'SA07', stream: 'Pb rougher tail → Zn rougher feed', frequency: 'Every 4 h (min. 2/shift)', method: 'manual', spec: 'Cross-check for Pb loss to Zn feed', latencyClass: 'shift', adherence: 'REG' },
  { id: 'SA08', stream: 'Zn rougher concentrate', frequency: 'Every 4 h (min. 2/shift)', method: 'manual', spec: '37-47% Zn (design band)', latencyClass: 'shift', adherence: 'REG' },
];

/** Thirteen-stream inventory — SA01–SA08 (S6, SA08 added `paul-call.A4` #253) plus five non-process streams (B: Sample Register). */
export const SAMPLE_SOURCES: readonly SampleSource[] = [
  ...SAMPLE_POINTS.map((point) => ({
    samplePointId: point.id,
    stream: point.stream,
    frequency: point.frequency,
    method: point.method as 'manual',
    spec: point.spec,
    latencyClass: point.latencyClass,
    adherence: point.adherence,
  })),
  {
    stream: 'Regrind mill product',
    frequency: 'Every 4 h (min. 2/shift)',
    method: 'manual',
    spec: 'P80 ≈ 30 µm target',
    latencyClass: 'shift',
    adherence: 'REG',
  }, // B: Sample Register
  {
    stream: 'Pb/Ag regrind feed',
    frequency: 'Every 4 h (min. 2/shift)',
    method: 'manual',
    spec: 'Feeds Pb cleaner recovery accounting',
    latencyClass: 'shift',
    adherence: 'REG',
  }, // B: Sample Register
  {
    stream: 'Process water',
    frequency: '1/shift composite',
    method: 'manual',
    spec: 'Entrained solids / clarity check',
    latencyClass: 'composite',
    adherence: 'REG',
  }, // B: Sample Register
  {
    stream: 'ZnSO₄ solution',
    frequency: '1/shift',
    method: 'manual',
    spec: 'Solution strength titration',
    latencyClass: 'shift',
    adherence: 'PENDING',
  }, // B: Sample Register
  {
    stream: 'SMBS solution',
    frequency: '1/shift',
    method: 'manual',
    spec: 'Solution strength titration',
    latencyClass: 'shift',
    adherence: 'PENDING',
  }, // B: Sample Register
];

export const SUBPROCESSES: readonly Subprocess[] = [
  {
    id: 'sp-crush',
    name: 'Ore receiving & crushing',
    sequenceOrder: 1,
    inputs: 'ROM ore, 1,800 stpd',
    outputs: 'Crushed ore to Fine Ore Bin, 75 stph design',
    recirculatingLoad: 'None identified',
    isBottleneck: false,
  },
  {
    id: 'sp-grind',
    name: 'Primary grinding (BM01 + CY01)',
    sequenceOrder: 2,
    inputs: `Fresh ore ${BM01_FEED_LABEL} (daily average) + cyclone underflow (internal)`,
    outputs: 'Design cyclone overflow ≈1,880 dst (flotation feed)',
    recirculatingLoad: `Design recycle — cyclone U/F to mill, 186 stph, 248% of ${BM01_FEED_DESIGN} fresh feed`,
    isBottleneck: true,
  },
  {
    id: 'sp-pb',
    name: 'Pb/Ag flotation (rougher → regrind → cleaner)',
    sequenceOrder: 3,
    inputs: 'Cyclone overflow, 1,880 dst',
    outputs: 'Pb/Ag concentrate 56.9 dst; Pb rougher tail 1,728 dst → Zn feed',
    recirculatingLoad: 'Internal — Pb cleaner tail to Pb rougher feed, 79.9 dst',
    isBottleneck: false,
  },
  {
    id: 'sp-zn',
    name: 'Zn flotation (rougher → cleaner)',
    sequenceOrder: 4,
    inputs: 'Pb rougher tail 1,728 dst + Zn cleaner tail (internal)',
    outputs: 'Zn concentrate 143.7 dst; Zn rougher tail 1,584 dst → tailings',
    recirculatingLoad: 'Internal — Zn cleaner tail to Zn rougher feed, 67.8 dst',
    isBottleneck: false,
  },
  {
    id: 'sp-filter',
    name: 'Concentrate thickening & filtration',
    sequenceOrder: 5,
    inputs: 'Pb/Ag con + Zn con slurry',
    outputs: 'Filtered Pb/Ag concentrate (67.0% Pb); filtered Zn concentrate (58.0% Zn)',
    recirculatingLoad: 'Cross-subprocess — thickener overflow recycles to own flotation circuit',
    isBottleneck: false,
  },
  {
    id: 'sp-paste',
    name: 'Tails thickening & paste backfill',
    sequenceOrder: 6,
    inputs: 'Zn rougher tail, 1,584 dst (88.0% of feed mass)',
    outputs: 'Paste backfill; thickener overflow',
    recirculatingLoad: 'Utility — overflow (451 gpm) feeds Process Water System',
    isBottleneck: false,
  },
  {
    id: 'sp-water',
    name: 'Process water system',
    sequenceOrder: 7,
    inputs: 'Tails/Pb/Zn thickener overflows + ~5 gpm fresh makeup',
    outputs: 'Distributed water to grinding, flotation, gland seals, reagent make-up',
    recirculatingLoad: 'Utility loop feeding 3+ upstream subprocesses',
    isBottleneck: false,
  },
];

// Word-doc §8.1 as a real graph: every flow is a directed edge between
// subprocess nodes (or 'external' at the plant boundary). Built from the
// mass-balance formula walk (§4) — SamplePoints attach to edges (§8.4).
export const STREAMS: readonly Stream[] = [
  { id: 'st-rom', from: 'external', to: 'sp-crush', name: 'ROM ore from mine', material: 'ore', nominalFlow: '1,800 stpd', flowReadingId: STREAM_FLOW_READING_IDS['st-rom'], isRecycle: false, samplePointIds: [] },
  { id: 'st-crushed', from: 'sp-crush', to: 'sp-grind', name: 'Crushed ore to fine ore bin', material: 'ore', nominalFlow: '75 stph design', flowReadingId: STREAM_FLOW_READING_IDS['st-crushed'], isRecycle: false, samplePointIds: [] },
  { id: 'st-cyc-uf', from: 'sp-grind', to: 'sp-grind', name: 'Cyclone underflow → ball mill', material: 'slurry', nominalFlow: '186 stph', flowReadingId: STREAM_FLOW_READING_IDS['st-cyc-uf'], isRecycle: true, samplePointIds: [] },
  { id: 'st-cyc-of', from: 'sp-grind', to: 'sp-pb', name: 'Cyclone overflow (flotation feed)', material: 'slurry', nominalFlow: '≈1,880 dst', isRecycle: false, samplePointIds: ['SA01'] },
  { id: 'st-pb-cln-tail', from: 'sp-pb', to: 'sp-pb', name: 'Pb cleaner tail → Pb rougher feed', material: 'slurry', nominalFlow: '79.9 dst', isRecycle: true, samplePointIds: [] },
  { id: 'st-pbcon', from: 'sp-pb', to: 'sp-filter', name: 'Pb/Ag concentrate', material: 'concentrate', nominalFlow: '56.9 dst', isRecycle: false, samplePointIds: ['SA03'] },
  { id: 'st-pbtail', from: 'sp-pb', to: 'sp-zn', name: 'Pb rougher tail → Zn rougher feed', material: 'slurry', nominalFlow: '1,728 dst', isRecycle: false, samplePointIds: ['SA07'] },
  { id: 'st-zn-cln-tail', from: 'sp-zn', to: 'sp-zn', name: 'Zn cleaner tail → Zn rougher feed', material: 'slurry', nominalFlow: '67.8 dst', isRecycle: true, samplePointIds: [] },
  { id: 'st-zncon', from: 'sp-zn', to: 'sp-filter', name: 'Zn concentrate', material: 'concentrate', nominalFlow: '143.7 dst', isRecycle: false, samplePointIds: ['SA04'] },
  { id: 'st-tails', from: 'sp-zn', to: 'sp-paste', name: 'Zn rougher tail (final tailings)', material: 'tailings', nominalFlow: '1,584 dst (88.0% of feed mass)', isRecycle: false, samplePointIds: ['SA05'] },
  { id: 'st-conc-of', from: 'sp-filter', to: 'sp-water', name: 'Pb/Zn thickener overflow', material: 'water', nominalFlow: '—', isRecycle: true, samplePointIds: [] },
  { id: 'st-tails-of', from: 'sp-paste', to: 'sp-water', name: 'Tails thickener overflow', material: 'water', nominalFlow: '451 gpm', isRecycle: true, samplePointIds: [] },
  { id: 'st-makeup', from: 'external', to: 'sp-water', name: 'Fresh make-up water', material: 'water', nominalFlow: '~5 gpm', isRecycle: false, samplePointIds: [] },
  { id: 'st-water-mill', from: 'sp-water', to: 'sp-grind', name: 'Mill water addition (FIT-022)', material: 'water', nominalFlow: '~300 gpm design', isRecycle: true, samplePointIds: [] },
  { id: 'st-water-pb', from: 'sp-water', to: 'sp-pb', name: 'Process water to Pb circuit (reagent make-up, gland seals)', material: 'water', nominalFlow: '—', isRecycle: true, samplePointIds: [] },
  { id: 'st-water-zn', from: 'sp-water', to: 'sp-zn', name: 'Process water to Zn circuit (reagent make-up, gland seals)', material: 'water', nominalFlow: '—', isRecycle: true, samplePointIds: [] },
  { id: 'st-pbcake', from: 'sp-filter', to: 'external', name: 'Filtered Pb/Ag concentrate to loadout', material: 'concentrate', nominalFlow: '—', isRecycle: false, samplePointIds: [] },
  { id: 'st-zncake', from: 'sp-filter', to: 'external', name: 'Filtered Zn concentrate to loadout', material: 'concentrate', nominalFlow: '—', isRecycle: false, samplePointIds: [] },
  { id: 'st-paste', from: 'sp-paste', to: 'external', name: 'Paste backfill to underground stopes', material: 'tailings', nominalFlow: '350 t/d target', isRecycle: false, samplePointIds: [] },
  // Interior equipment-level edges within sp-pb (SA02 rougher feed · SA06 rougher conc).
  { id: 'st-pb-int-sa02', from: 'sp-pb', to: 'PB01-RGH', name: 'Pb rougher feed', material: 'slurry', nominalFlow: '—', isRecycle: false, samplePointIds: ['SA02'] },
  { id: 'st-pb-int-sa06', from: 'PB01-RGH', to: 'PB01-RGR', name: 'Pb rougher concentrate', material: 'concentrate', nominalFlow: '—', isRecycle: false, samplePointIds: ['SA06'] },
];

// §8.1 recirculating-load column, one row per loop, scope preserved.
export const RECIRCULATING_LOOPS: readonly RecirculatingLoop[] = [
  { id: 'loop-cyc', streamId: 'st-cyc-uf', scope: 'internal', loadRatio: '248% of new feed', note: 'The plant’s own leading indicator — a rising trend means grind is coarsening.' },
  { id: 'loop-pb-cln', streamId: 'st-pb-cln-tail', scope: 'internal', loadRatio: '', note: 'Cleaner-tail recycle building signals poor first-pass grade or recovery.' },
  { id: 'loop-zn-cln', streamId: 'st-zn-cln-tail', scope: 'internal', loadRatio: '', note: 'Same pattern as the Pb cleaner loop, Zn side.' },
  { id: 'loop-conc-of', streamId: 'st-conc-of', scope: 'cross-subprocess', loadRatio: '', note: 'Thickener overflow returns as process water — entrained solids here degrade water quality plant-wide.' },
  { id: 'loop-tails-of', streamId: 'st-tails-of', scope: 'utility', loadRatio: '451 gpm', note: 'Primary feed to the Process Water System.' },
  { id: 'loop-water', streamId: 'st-water-mill', scope: 'utility', loadRatio: '', note: 'Water loop feeds 3+ upstream subprocesses simultaneously — one quality issue affects the whole plant.' },
];

// §8.4 specs + deck slide 8 penalty limits — projected from READINGS.
export const STREAM_SPECS: readonly StreamSpec[] = READINGS.filter(
  (row) => row.id.startsWith('rd-spec-'),
).map((row) => specFromReading(row, STREAM_SPEC_COPY[row.id] ?? { consequence: '' }));

export const PROCESS_CONSTRAINTS: readonly ProcessConstraint[] = READINGS.filter(
  (row) => row.id.startsWith('rd-c-'),
).map(constraintFromReading);

export const UTILIZATION_RANKS: readonly UtilizationRank[] = [
  {
    id: 'u-grind',
    name: 'Primary grinding (mill + cyclone)',
    basis:
      '75 stph / 90 stph normal band · 4 of 5 cyclones · 248% circulating load · 261 stph through-flow (75 new + 186 recycle)',
    isBottleneck: true,
    scope: 'subprocess',
    readingId: 'rd-c-feed',
    computed: true,
  },
  {
    id: 'u-cyc',
    name: 'Cyclone cluster spare capacity',
    utilizationPct: 80,
    basis: '4 of 5 in service at design — one spare only',
    isBottleneck: false,
    scope: 'component',
    readingId: 'rd-util-cyc',
  },
  {
    id: 'u-crush',
    name: 'Ore receiving & crushing',
    basis: '75 of 150 tph normal band',
    isBottleneck: false,
    scope: 'subprocess',
    readingId: 'rd-c-crush',
    computed: true,
  },
  {
    id: 'u-mill-kw',
    name: 'Ball mill motor power',
    utilizationPct: 67,
    basis: 'Design draw vs 1,050 kW normal / 1,500 kW DCS max',
    isBottleneck: false,
    scope: 'component',
    readingId: 'rd-c-kw',
  },
  {
    id: 'u-pb',
    name: 'Pb/Ag flotation',
    utilizationPct: 44,
    basis: 'Through-flow vs cell-bank design — not the constraint',
    isBottleneck: false,
    scope: 'subprocess',
    readingId: 'rd-util-pb',
  },
  {
    id: 'u-zn',
    name: 'Zn flotation',
    utilizationPct: 48,
    basis: 'Through-flow vs cell-bank design — not the constraint',
    isBottleneck: false,
    scope: 'subprocess',
    readingId: 'rd-util-zn',
  },
];

export const BOTTLENECK_READ =
  'Design-basis: grinding (mill + cyclone) is the tightest subprocess — 75 stph sits at 83% of the 90 stph normal band; 4 of 5 cyclones in service; 248% circulating load.';

// Concentrator Process Flow UI Concept, transcribed per process box:
// good looks like / bad looks like / operator insights, tag-grounded.
// Tags are the UI-concept instrument dialect (WIT/JT/DT…), not the
// Appendix B loop dialect (WIC/JIC/PI…) — mapping is corpus item D5.
// Crushing is not covered by the UI-concept doc (starts at grinding).
export const NODE_INSIGHTS: readonly NodeInsight[] = [
  // Grinding circuit (ball mill / cyclone cluster / regrind)
  { id: 'ni-gr-g1', subprocessId: 'sp-grind', kind: 'good', text: 'Cyclone feed density and pressure stable within the design band.', tags: ['DT-203', 'PT-173'] },
  { id: 'ni-gr-g2', subprocessId: 'sp-grind', kind: 'good', text: 'Cyclone pump box level holding mid-range, no surging.', tags: ['LT-169', 'LT-171', 'LT-014'] },
  { id: 'ni-gr-g3', subprocessId: 'sp-grind', kind: 'good', text: 'Mill motor power steady — consistent ore competency and load.', tags: ['JT-023'] },
  { id: 'ni-gr-g4', subprocessId: 'sp-grind', kind: 'good', text: 'Cyclone overflow consistently at ~37% solids feeding flotation.', tags: [] },
  { id: 'ni-gr-b1', subprocessId: 'sp-grind', kind: 'bad', text: 'Cyclone feed pressure cycling — sign of roping or surging cyclones.', tags: ['PT-173', 'PT-175'] },
  { id: 'ni-gr-b2', subprocessId: 'sp-grind', kind: 'bad', text: 'Pump box level trending to high or low alarm — cavitation or overflow risk.', tags: ['LT-169', 'LT-171', 'LT-014', 'LT-100'] },
  { id: 'ni-gr-b3', subprocessId: 'sp-grind', kind: 'bad', text: 'Mill power dropping with constant feed — possible liner wear or ball charge issue.', tags: ['JT-023'] },
  { id: 'ni-gr-b4', subprocessId: 'sp-grind', kind: 'bad', text: 'Regrind cyclone feed density drifting — grind size off the ~30 µm target.', tags: ['DT-605'] },
  { id: 'ni-gr-i1', subprocessId: 'sp-grind', kind: 'insight', text: 'Belt scales WIT-021 (mill feed) and WIT-009 (silo feed) are the primary mass-balance anchors — reconcile against cyclone feed flow.', tags: ['WIT-021', 'WIT-009', 'FIT-203'] },
  { id: 'ni-gr-i2', subprocessId: 'sp-grind', kind: 'insight', text: 'Loss of conveyor speed signal often precedes a no-feed event.', tags: ['ST-201', 'ST-109'] },
  { id: 'ni-gr-i3', subprocessId: 'sp-grind', kind: 'insight', text: 'Treat circulating load (~186 tph, ~248%) as a leading indicator — a rising trend suggests grind is coarsening.', tags: [] },
  // Pb conditioning & flotation (rougher + cleaners 1–3)
  { id: 'ni-pb-g1', subprocessId: 'sp-pb', kind: 'good', text: 'Pb rougher pH stable in target range — correct lime/depressant dosing.', tags: ['AT-185'] },
  { id: 'ni-pb-g2', subprocessId: 'sp-pb', kind: 'good', text: 'Cell levels steady at setpoint — froth depth consistent.', tags: ['LT-140', 'LT-141'] },
  { id: 'ni-pb-g3', subprocessId: 'sp-pb', kind: 'good', text: 'Flotation air flow steady, producing a consistent froth carry rate.', tags: ['FIT-131'] },
  { id: 'ni-pb-g4', subprocessId: 'sp-pb', kind: 'good', text: 'Cleaner concentrate grade trending toward the final Pb con sample target.', tags: [] },
  { id: 'ni-pb-b1', subprocessId: 'sp-pb', kind: 'bad', text: 'pH drifting low — risk of Zn activation / poor depression, dropping Pb concentrate grade.', tags: ['AT-185'] },
  { id: 'ni-pb-b2', subprocessId: 'sp-pb', kind: 'bad', text: 'Cell levels surging — over/under air or a feed disturbance.', tags: ['LT-140', 'LT-141'] },
  { id: 'ni-pb-b3', subprocessId: 'sp-pb', kind: 'bad', text: 'Cleaner feed box recycle building — cleaner circulating load too high; poor first-pass grade or recovery.', tags: [] },
  { id: 'ni-pb-b4', subprocessId: 'sp-pb', kind: 'bad', text: 'Reagent dosing pumps tripped — check pump status.', tags: [] },
  { id: 'ni-pb-i1', subprocessId: 'sp-pb', kind: 'insight', text: 'Five sample streams (flot feed, Pb con, cleaner con, cleaner tail, rougher tail) track recovery and grade through the circuit.', tags: [] },
  { id: 'ni-pb-i2', subprocessId: 'sp-pb', kind: 'insight', text: 'ZnSO₄/SMBS dose the conditioner; MIBC/3418A distribute to rougher feed and each cleaner — verify pump-to-destination mapping.', tags: [] },
  { id: 'ni-pb-i3', subprocessId: 'sp-pb', kind: 'insight', text: 'The optional regrind bypass changes cleaner feed grind size — correlate with grind-size or throughput excursions.', tags: [] },
  // Zn conditioning & flotation (rougher, scavenger, cleaners 1–3)
  { id: 'ni-zn-g1', subprocessId: 'sp-zn', kind: 'good', text: 'Zn rougher and 1st cleaner pH within the target activation range.', tags: ['AT-150', 'AT-176'] },
  { id: 'ni-zn-g2', subprocessId: 'sp-zn', kind: 'good', text: 'Cell levels stable at setpoint.', tags: ['LT-160', 'LT-161', 'LT-181', 'LT-182'] },
  { id: 'ni-zn-g3', subprocessId: 'sp-zn', kind: 'good', text: 'Air flows consistent — stable froth and recovery.', tags: ['FT-162', 'FT-163', 'FT-177', 'FT-178', 'FIT-158'] },
  { id: 'ni-zn-g4', subprocessId: 'sp-zn', kind: 'good', text: 'Zn concentrate grade tracking the design assay.', tags: [] },
  { id: 'ni-zn-b1', subprocessId: 'sp-zn', kind: 'bad', text: 'Zn rougher pH too high — risk of Pb/Fe activation reducing Zn concentrate grade.', tags: ['AT-150'] },
  { id: 'ni-zn-b2', subprocessId: 'sp-zn', kind: 'bad', text: 'Cell level alarms — feed surge or air imbalance.', tags: ['LT-125'] },
  { id: 'ni-zn-b3', subprocessId: 'sp-zn', kind: 'bad', text: 'Zn cleaner tail grade rising — recoverable Zn reporting to final tails.', tags: [] },
  { id: 'ni-zn-b4', subprocessId: 'sp-zn', kind: 'bad', text: 'CuSO₄ pump status fault — under-activation of Zn.', tags: [] },
  { id: 'ni-zn-i1', subprocessId: 'sp-zn', kind: 'insight', text: 'CuSO₄ dosing to the conditioner is the key activation step — any disruption cascades through rougher and cleaner grades.', tags: [] },
  { id: 'ni-zn-i2', subprocessId: 'sp-zn', kind: 'insight', text: 'Stage samples (rougher con, rougher tail, cleaner tail) locate where Zn losses occur.', tags: [] },
  // Concentrate thickening & filtration (Pb and Zn)
  { id: 'ni-fl-g1', subprocessId: 'sp-filter', kind: 'good', text: 'Thickener rake torque in the normal band — no bed compaction.', tags: ['OT-200', 'OT-250'] },
  { id: 'ni-fl-g2', subprocessId: 'sp-filter', kind: 'good', text: 'Filter feed density consistent — stable cake formation.', tags: ['DT-252', 'DT-253'] },
  { id: 'ni-fl-g3', subprocessId: 'sp-filter', kind: 'good', text: 'Vacuum pressure holding target — good cake dewatering.', tags: ['PIT-210'] },
  { id: 'ni-fl-g4', subprocessId: 'sp-filter', kind: 'good', text: 'Stacker/loadout conveyor weights tracking expected concentrate production.', tags: ['WT-301', 'WT-272'] },
  { id: 'ni-fl-b1', subprocessId: 'sp-filter', kind: 'bad', text: 'Rake torque climbing — bed building, rake stall risk; check flocculant dosing and underflow pump rate.', tags: ['OT-200', 'OT-250'] },
  { id: 'ni-fl-b2', subprocessId: 'sp-filter', kind: 'bad', text: 'Filter feed density dropping — underflow too dilute, filter throughput will fall.', tags: ['DT-252', 'DT-253'] },
  { id: 'ni-fl-b3', subprocessId: 'sp-filter', kind: 'bad', text: 'Vacuum pressure falling — cloth blinding or a vacuum pump issue.', tags: ['PIT-210'] },
  { id: 'ni-fl-b4', subprocessId: 'sp-filter', kind: 'bad', text: 'Rake height trending up — bed level rising toward the overflow weir.', tags: ['ZT-201', 'ZT-251'] },
  { id: 'ni-fl-i1', subprocessId: 'sp-filter', kind: 'insight', text: 'Thickener overflow recycles as process water — monitor entrained-solids carryover into downstream water quality.', tags: [] },
  { id: 'ni-fl-i2', subprocessId: 'sp-filter', kind: 'insight', text: 'Loadout conveyor data cross-checks mass-balance concentrate production rates.', tags: ['WT-272', 'ST-202', 'WT-301'] },
  { id: 'ni-fl-i3', subprocessId: 'sp-filter', kind: 'insight', text: 'Irregular vacuum pump cycling is an early indicator of filter cloth or valve issues.', tags: [] },
  // Tails handling (thickener, filter, pump box)
  { id: 'ni-ts-g1', subprocessId: 'sp-paste', kind: 'good', text: 'Tails thickener overflow clarity good — minimal solids carryover into recycled water.', tags: [] },
  { id: 'ni-ts-g2', subprocessId: 'sp-paste', kind: 'good', text: 'Filter feed tank level stable — no surging to the filter.', tags: [] },
  { id: 'ni-ts-g3', subprocessId: 'sp-paste', kind: 'good', text: 'Filtrate clear — good cake formation, minimal fines bleed-through.', tags: [] },
  { id: 'ni-ts-b1', subprocessId: 'sp-paste', kind: 'bad', text: 'Overflow turbid — flocculant under-dosed or thickener overloaded; contaminates recycled process water.', tags: [] },
  { id: 'ni-ts-b2', subprocessId: 'sp-paste', kind: 'bad', text: 'Tails pump box level trending to alarm — tails backing into flotation or filter starvation.', tags: [] },
  { id: 'ni-ts-b3', subprocessId: 'sp-paste', kind: 'bad', text: 'Filtrate carrying high solids — cloth wear or flocculant dosing off upstream.', tags: [] },
  { id: 'ni-ts-i1', subprocessId: 'sp-paste', kind: 'insight', text: 'Tails overflow is a major process-water source to the mill — contamination directly affects grind water quality and reagent chemistry.', tags: [] },
  { id: 'ni-ts-i2', subprocessId: 'sp-paste', kind: 'insight', text: 'Track filter wash water and filtrate separately in the water balance — both return to process water.', tags: [] },
  // Plant process water & gland water system
  { id: 'ni-wt-g1', subprocessId: 'sp-water', kind: 'good', text: 'Process water tank levels stable — recycled sources balancing plant demand.', tags: [] },
  { id: 'ni-wt-g2', subprocessId: 'sp-water', kind: 'good', text: 'Mill water addition tracking the ~300 gpm setpoint for target cyclone feed density.', tags: ['FIT-022'] },
  { id: 'ni-wt-g3', subprocessId: 'sp-water', kind: 'good', text: 'Gland water chiller holding supply temperature — no pump seal failures.', tags: [] },
  { id: 'ni-wt-b1', subprocessId: 'sp-water', kind: 'bad', text: 'Process water tank running low — insufficient thickener overflow recycle; raw make-up water needed.', tags: [] },
  { id: 'ni-wt-b2', subprocessId: 'sp-water', kind: 'bad', text: 'Mill water addition off setpoint — cyclone feed density drifts off design, affecting grind.', tags: ['FIT-022', 'DT-203'] },
  { id: 'ni-wt-b3', subprocessId: 'sp-water', kind: 'bad', text: 'Chiller underperforming — gland water too warm, pump seal damage risk.', tags: [] },
  { id: 'ni-wt-i1', subprocessId: 'sp-water', kind: 'insight', text: 'Mill water addition directly sets cyclone feed % solids — one of the most sensitive single control points for grind size.', tags: ['FIT-022'] },
  { id: 'ni-wt-i2', subprocessId: 'sp-water', kind: 'insight', text: 'All three thickener overflows feed one process water system — a quality issue in any one affects the whole plant.', tags: [] },
];

// A §M1 §9 — historian + LIMS are "needed next"; deck slide 12's "live" strip stays on record as the target state
export const CRO_STATUS_LINE =
  'DCS online · Historian: connection pending · LIMS: manual assays · Design basis loaded · AI engine active';

// Slide 05 source-status strip — batch extracts loaded alongside the live-source line
// above (30-day historian extract + 8-Jun ore assay set; live historian stays pending)
export const SOURCE_EXTRACTS_LINE = 'Historical DB 30-day · Ore assays 8-Jun';

export const EXTRA_ROLE_TABLES: readonly RoleTable[] = [
  {
    roleId: 'gm',
    tabId: 'overview',
    caption: 'Concentrate summary (this month)',
    columns: ['Product', 'Grade', 'Notes'],
    rows: [
      // gm-c1's value cell used to carry a hardcoded "(design 58.0%)" suffix
      // — the same class of literal #258 MUST-FIX 9 was opened to remove.
      // The design figure now has exactly one source: the meter marker in
      // RoleLandingPanels.transform.ts (gmZincConcentrateCard, `pl.D1` #142
      // round 4), read from rd-kpi-zn-grade. Stripped here rather than
      // regex-stripped in the transform, per the same round's ruling.
      { id: 'gm-c1', cells: ['Zinc concentrate', '58.4% Zn', 'Meets spec · ~3,960 t/month'] },
      // Fallback text only — gmPbAgConcentrateCard() computes the live "vs
      // <basis> <value>" clause from readingComparisonBasis(rd-kpi-ag-grade)
      // so this cell can't go stale again the way the pre-round-2 literal
      // "vs Ramp 12.13" did (pl.D1 round 2, #142).
      { id: 'gm-c2', cells: ['Pb/Ag concentrate', 'Pb 67.1% · Ag 12.14 oz/t', 'vs Budget 12.13 · design 12.13'] },
      { id: 'gm-c3', cells: ['Monthly Ag', '~17,800 oz · @$30/oz ≈ $0.53M', 'Watch 3418A dosing'] },
    ],
  },
  {
    roleId: 'superintendent',
    tabId: 'plant-wide',
    caption: 'Area health · crusher to Zn flotation',
    columns: ['Area', 'Status', 'Rate / recovery', 'Note'],
    rows: [
      { id: 'sup-a1', cells: ['Surface crusher', 'OK', '680 t/h', '100% avail'] },
      { id: 'sup-a2', cells: ['Ball mill (primary)', 'WARN', BM01_FEED_LABEL, `P80 = ${formatValue(seedReading('rd-spec-p80'), { withUnit: true })}`] },
      { id: 'sup-a3', cells: ['Regrind mill', 'OK', '18 t/h conc', 'P80 = 30 µm'] },
      { id: 'sup-a4', cells: ['Pb flotation', 'OK', `Pb rec ${PB_RECOVERY_TILE.value}`, 'pH 8.4'] },
      { id: 'sup-a5', cells: ['Zn flotation', 'OK', 'Zn rec 82.3%', 'pH 10.8'] },
    ],
  },
  {
    roleId: 'superintendent',
    tabId: 'plant-wide',
    caption: 'Reagent tank levels',
    columns: ['Reagent', 'Level', 'Duty'],
    rows: [
      { id: 'sup-t1', cells: ['ZnSO₄', '78%', 'Pb depressant'] },
      { id: 'sup-t2', cells: ['SMBS', '65%', 'Pb/Py depressant'] },
      { id: 'sup-t3', cells: ['CuSO₄', '88%', 'Zn activator'] },
      { id: 'sup-t4', cells: ['MIBC', '55%', 'Frother'] },
      { id: 'sup-t5', cells: ['3418A', '82%', 'Collector'] },
      { id: 'sup-t6', cells: ['Aero 5100', '72%', 'Promoter'] },
    ],
  },
  {
    roleId: 'superintendent',
    tabId: 'crew',
    caption: 'Crew compliance by category · 7 complete days · 01–07 Jun 2026',
    columns: ['Crew', 'Shifts', 'Samples', 'Rounds', 'Reports', 'Reagents', 'LIMS TAT', 'Overall'],
    rows: [
      { id: 'crew-a', cells: ['A Crew (Day)', '4', '22/25', '94.1%', '100%', '96%', '1.8h', '94.6%'] },
      { id: 'crew-b', cells: ['B Crew (Night)', '3', '14/17', '88.5%', '100%', '90%', '2.4h', '90.2%'] },
      { id: 'crew-c', cells: ['C Crew (Day)', '4', '23/25', '95.8%', '100%', '98%', '1.5h', '96.3%'] },
      { id: 'crew-d', cells: ['D Crew (Night)', '3', '14/18', '84.2%', '100%', '88%', '2.9h', '88.0%'] },
      // Derived by crewTableWithRollingLabel from the four crew rows above; these
      // literals are the same figures so no second value survives in the seed.
      { id: 'cr-t', cells: ['Total · crew avg', '14', '73/85', '90.7%', '100%', '93%', '2.2h', '92.3%'] },
    ],
  },
  {
    roleId: 'superintendent',
    tabId: 'crew',
    caption: 'Top compliance gaps (this week)',
    columns: ['Count', 'Non-compliance', 'Crew'],
    rows: [
      { id: 'cg-1', cells: ['8', 'Samples collected but not registered in LIMS within 1h', 'B & D'] },
      { id: 'cg-2', cells: ['6', 'Zn rougher conc 10:00 sample missed (pH intervention)', 'B'] },
      { id: 'cg-3', cells: ['5', 'Operator rounds completed >30 min late', 'D'] },
      { id: 'cg-4', cells: ['3', 'pH meter calibration not recorded in shift log', 'All'] },
      { id: 'cg-5', cells: ['2', 'Final tails sample missed — pump intervention', 'B'] },
    ],
  },
  {
    roleId: 'metallurgist',
    tabId: 'recovery',
    caption: 'Reagent consumption (g/t ore) · actual vs design',
    columns: ['Reagent', 'Actual', 'Design', 'Δ', 'Circuit'],
    rows: [
      { id: 'rg-1', cells: ['ZnSO₄', '310', '300', '+3%', 'Pb'] },
      { id: 'rg-2', cells: ['SMBS', '195', '200', '−3%', 'Pb'] },
      { id: 'rg-3', cells: ['CuSO₄', '155', '150', '+3%', 'Zn'] },
      { id: 'rg-4', cells: ['MIBC', '24', '25', '−4%', 'Both'] },
      { id: 'rg-5', cells: ['3418A', '42', '45', '−7%', 'Pb'] },
      { id: 'rg-6', cells: ['Aero 5100', '31', '35', '−12%', 'Zn'] },
      { id: 'rg-7', cells: ['Lime', '2,100', '2,000', '+5%', 'pH'] },
    ],
  },
  {
    roleId: 'metallurgist',
    tabId: 'recovery',
    caption: '7-day recovery vs design (table, not a chart)',
    columns: ['Day', 'Zn %', 'Pb %', 'Ag %'],
    rows: buildRecoveryTableRows(),
  },
  {
    roleId: 'metallurgist',
    tabId: 'samples',
    caption: 'Sample register · today vs LIMS (all manual — no auto-samplers)',
    columns: ['Stream', 'Freq', 'Last collected', 'LIMS', 'Status', 'TAT', 'Method', 'Tier'],
    rows: [
      { id: 'sr-1', cells: ['ROM head', '2-hrly', '10:05', '09:52 (08:00)', 'LATE', '1.9h', 'Manual', 'Shift < 3 h'] },
      { id: 'sr-2', cells: ['Pb/Ag concentrate', '2-hrly', '10:08', '10:14', 'REG', '0.1h', 'Manual', 'Rush < 1 h'] },
      { id: 'sr-3', cells: ['Pb tails / Zn feed', '2-hrly', '10:10', '10:18', 'REG', '0.1h', 'Manual', 'Shift < 3 h'] },
      { id: 'sr-4', cells: ['Zn concentrate', '2-hrly', '10:07', '10:15', 'REG', '0.1h', 'Manual', 'Rush < 1 h'] },
      { id: 'sr-5', cells: ['Final tails', '2-hrly', '10:12', '—', 'LIMS?', '—', 'Manual', 'Shift < 3 h'] },
      { id: 'sr-6', cells: ['Pb rougher concentrate', '4-hrly', '10:09', '10:21', 'REG', '0.2h', 'Manual', 'Shift < 3 h'] }, // B: slide 07
      { id: 'sr-7', cells: ['Zn rougher conc', '4-hrly', '06:14', 'MISS 10:00', 'MISS', '—', 'Manual', 'Shift < 3 h'] },
      { id: 'sr-8', cells: ['Regrind mill product', '1/shift', '06:18', '06:31', 'REG', '0.2h', 'Manual', 'Shift < 3 h'] }, // B: slide 07
      { id: 'sr-9', cells: ['Pb/Ag regrind feed', '4-hrly', '10:11', '10:19', 'REG', '0.1h', 'Manual', 'Shift < 3 h'] }, // B: slide 07
      { id: 'sr-10', cells: ['Process water', '1/shift', '06:22', '06:40', 'REG', '0.3h', 'Manual', '24 h composite'] }, // B: slide 07
      { id: 'sr-11', cells: ['ZnSO₄ solution', '1/shift', '08:14', '—', 'LIMS?', '—', 'Manual', 'Shift < 3 h'] },
      { id: 'sr-12', cells: ['SMBS solution', '1/shift', '08:16', '—', 'LIMS?', '—', 'Manual', 'Shift < 3 h'] },
    ],
  },
  {
    roleId: 'metallurgist',
    tabId: 'samples',
    caption: 'Process setpoint % of shift in range (target 90%)',
    columns: ['Parameter', '% shift in range', 'Status'],
    rows: [
      { id: 'sp-1', cells: ['Zn flotation pH', '68%', 'WARN'] },
      { id: 'sp-2', cells: ['Pb flotation pH', '91%', 'OK'] },
      { id: 'sp-3', cells: ['Mill discharge density', '88%', 'OK'] },
      { id: 'sp-4', cells: ['Grind P80', '74%', 'WARN'] },
      { id: 'sp-5', cells: ['Zn float air flow', '91%', 'OK'] },
      { id: 'sp-6', cells: ['Pb float air flow', '85%', 'OK'] },
    ],
  },
  {
    roleId: 'metallurgist',
    tabId: 'samples',
    caption: 'Reagent inventory · days of supply',
    columns: ['Reagent', 'Days remaining', 'Note'],
    rows: [
      { id: 'ri-1', cells: ['ZnSO₄', '18d', 'Pb depressant'] },
      { id: 'ri-2', cells: ['SMBS', '11d', 'Pb/Py depressant'] },
      { id: 'ri-3', cells: ['CuSO₄', '22d', 'Zn activator'] },
      { id: 'ri-4', cells: ['MIBC', '8d', 'Frother'] },
      { id: 'ri-5', cells: ['3418A', '25d', 'Pb collector'] },
      { id: 'ri-6', cells: ['Aero 5100', '6d', 'REORDER'] },
    ],
  },
  // 'Mass balance closure · today (tolerance ±3%)' removed komatsu.F1 / #727 —
  // its typed error and status cells (Zn +1.8% · Pb −2.4% · Ag −5.1%) were
  // fabricated and disagreed with the engine for all three metals. Quality &
  // Lab's closure widget now reads the day balance's displayed-precision
  // closure per metal (`massBalanceMeters` → `displayedClosureGapPct`), the
  // same engine read Metal accounting's closure panel prints.
  // 'Smelter contract limits · impurity penalties' removed paul-fmea.A15 /
  // #280 — it fabricated assayed "Current" values for Sb, Bi and Cd
  // (im-2/im-3/im-5), three of the eight penalty elements Bunker Hill does
  // not actually assay (MD-060). Superseded by `ConcentrateSpec`
  // (`bunker-hill-concentrate-specs.ts`), rendered by
  // `ConcentratePenaltyPanel`, which carries the real 3-of-11 assay count
  // (arsenic on both concentrates, plus Zn in the Pb concentrate — measured
  // but with no smelter penalty limit named for it).
  {
    roleId: 'metallurgist',
    tabId: 'impurities',
    caption: 'As distribution by product (today)',
    columns: ['Product', 'Share', 'Note'],
    rows: [
      { id: 'ad-1', cells: ['Final tails', '89%', 'Majority to tails — good'] },
      { id: 'ad-2', cells: ['Pb/Ag conc', '7%', '0.148 t As/day · trending up'] },
      { id: 'ad-3', cells: ['Zn conc', '4%', '0.094 t As/day · stable'] },
    ],
  },
  {
    roleId: 'ops-supervisor',
    tabId: 'equipment',
    caption: 'Shift task list',
    columns: ['Status', 'Task'],
    rows: [
      { id: 'st-1', cells: ['✓', 'Pb rougher feed rate to plan'] },
      { id: 'st-2', cells: ['✓', 'Lime dosing set — Zn pH target 10.8'] },
      { id: 'st-3', cells: ['✓', '08:00 concentrate samples dispatched'] },
      { id: 'st-4', cells: ['⟳', 'Investigate Zn circuit pH drift'] },
      { id: 'st-5', cells: ['⟳', 'Check 3418A dosing — Ag grade falling six shifts'] }, // K20 round 1 (#717) — the premise is the fall, not a level below design
      { id: 'st-6', cells: ['○', '10:00 manual density readings'] },
      { id: 'st-7', cells: ['○', 'Paste backfill target confirm w/ mine'] },
      { id: 'st-8', cells: ['○', 'End-of-shift handover report'] },
    ],
  },
  {
    roleId: 'ops-supervisor',
    tabId: 'shift-compliance',
    caption: 'Sample tracking · shift 06:00–18:00 (now 11:30)', // SA-F25: 'ALL manual — no auto-samplers' dropped -- cadence is session-global and contradicted the moment a Metallurgist flips the OSA toggle
    columns: ['Stream', '06:00', '08:00', '10:00', '12:00', '14:00', '16:00'],
    rows: [
      { id: 'ss-1', cells: ['ROM head (feed)', 'REG', 'REG', 'LIMS?', 'DUE', '--', '--'] }, // B: slide 10
      { id: 'ss-2', cells: ['Pb/Ag concentrate', 'REG', 'REG', 'REG', 'DUE', '--', '--'] }, // B: slide 10
      { id: 'ss-3', cells: ['Pb tails / Zn feed', 'REG', 'REG', 'REG', 'DUE', '--', '--'] }, // B: slide 10 · S11: register wins over deck cell
      { id: 'ss-4', cells: ['Zn concentrate', 'REG', 'REG', 'REG', 'DUE', '--', '--'] }, // B: slide 10
      { id: 'ss-5', cells: ['Final tails', 'REG', 'REG', 'LIMS?', 'DUE', '--', '--'] }, // B: slide 10
      { id: 'ss-6', cells: ['Pb rougher conc (4-hrly)', 'REG', 'n/a', 'REG', 'n/a', '--', 'n/a'] }, // B: slide 10
      { id: 'ss-7', cells: ['Zn rougher conc (4-hrly)', 'REG', 'n/a', 'MISS', 'n/a', '--', 'n/a'] }, // B: slide 10 · S11 canonical MISS
      { id: 'ss-8', cells: ['Regrind mill product (1/shift)', 'REG', 'n/a', 'n/a', 'n/a', 'n/a', 'n/a'] }, // B: slide 10 · SAMPLE_SOURCES
      { id: 'ss-9', cells: ['Process water (1/shift)', 'LIMS?', 'n/a', 'n/a', 'n/a', 'n/a', 'n/a'] }, // B: slide 10 · SAMPLE_SOURCES
      { id: 'ss-10', cells: ['ZnSO₄ solution (1/shift)', 'n/a', 'LIMS?', 'n/a', 'n/a', 'n/a', 'n/a'] }, // B: slide 10 · SAMPLE_SOURCES
      { id: 'ss-11', cells: ['SMBS solution (1/shift)', 'n/a', 'LIMS?', 'n/a', 'n/a', 'n/a', 'n/a'] }, // B: slide 10 · SAMPLE_SOURCES
    ],
  },
  {
    roleId: 'ops-supervisor',
    tabId: 'shift-compliance',
    caption: 'Operator rounds · every 2 hours',
    columns: ['Area', '06:00', '08:00', '10:00', '12:00'],
    rows: [
      { id: 'or-1', cells: ['Crusher/feed', 'REG', 'REG', 'REG', 'DUE'] },
      { id: 'or-2', cells: ['Ball mill/regrind', 'REG', 'REG', 'REG', 'DUE'] },
      { id: 'or-3', cells: ['Pb flotation', 'REG', 'REG', 'LATE', 'DUE'] },
      { id: 'or-4', cells: ['Zn flotation', 'REG', 'REG', 'REG', 'DUE'] },
      { id: 'or-5', cells: ['Reagent dosing', 'REG', 'REG', 'LIMS?', 'DUE'] },
      { id: 'or-6', cells: ['Thickener/filter', 'REG', 'REG', 'REG', 'DUE'] },
      { id: 'or-7', cells: ['Tailings & paste', 'REG', 'REG', 'REG', 'DUE'] },
    ],
  },
  {
    roleId: 'ops-supervisor',
    tabId: 'shift-compliance',
    caption: 'Outstanding actions',
    columns: ['Priority', 'Action', 'Who', 'Due'],
    rows: [
      {
        id: 'oa-1',
        cells: ['URGENT', 'Register final tails 10:00 in LIMS immediately', 'M. Reid', '08 Jun 11:45'],
      }, // B: slide 10 · Who/Due [syn]
      {
        id: 'oa-2',
        cells: ['URGENT', 'Register ZnSO₄ & SMBS solution in LIMS', 'M. Reid', '08 Jun 12:00'],
      }, // B: slide 10 · Who/Due [syn]
      {
        id: 'oa-3',
        cells: ['HIGH', 'Investigate Zn rougher 10:00 — missed sample', 'S. Park', '08 Jun 14:00'],
      }, // B: slide 10 · Who/Due [syn]
      {
        id: 'oa-4',
        cells: ['MED', 'Submit housekeeping log for 10:30 round', 'M. Reid', '08 Jun 12:30'],
      }, // B: slide 10 · Who/Due [syn]
    ],
  },
  {
    roleId: 'maintenance',
    tabId: 'overview',
    caption: 'Equipment health index',
    columns: ['Asset', 'Health'],
    rows: EQUIPMENT_HEALTH_INDEX.map((row) => ({
      id: row.id,
      cells: [row.asset, `${row.healthPct}%`],
    })),
  },
  {
    roleId: 'maintenance',
    tabId: 'overview',
    caption: 'Upcoming planned maintenance (14 days)', // [syn] A §M9 ¶6 — no shutdown calendar in document set
    columns: ['Date', 'Asset', 'Task', 'Est h', 'Crew'],
    rows: [
      { id: 'pm-2', cells: ['12 Jun', 'Regrind mill', 'Liner inspection', '4h', 'J. Smith'] }, // [syn] A §M9 ¶6
      { id: 'pm-3', cells: ['13 Jun', 'Pb/Ag filter', 'Filter cloth change', '8h', 'K. Jones'] }, // [syn] A §M9 ¶6
      { id: 'pm-4', cells: ['15 Jun', 'Pb recleaner', 'Air sparger rebuild complete', '6h', 'K. Jones'] }, // [syn] A §M9 ¶6
      { id: 'pm-5', cells: ['16 Jun', 'Tails pump 4', 'Seal water regulator overhaul', '5h', 'T. Brown'] }, // [syn] A §M9 ¶6
    ],
  },
  {
    roleId: 'operator',
    tabId: 'overview',
    caption: 'Record & submit',
    columns: ['Parameter', 'Reading', 'Status', 'Normal range'],
    rows: [
      { id: 'rd-1', cells: ['Ball mill fresh feed', BM01_FEED_LABEL, 'OK', BM01_FEED_BAND] },
      { id: 'rd-2', cells: ['Mill discharge density', '72.1%', 'OK', '70–74%'] },
      { id: 'rd-3', cells: ['Regrind P80', '30 µm', 'OK', '27–33 µm'] },
      { id: 'rd-4', cells: ['Pb cell pH', '8.4', 'OK', '8.2–8.6'] },
      { id: 'rd-5', cells: ['Zn cell pH', '10.2', 'WARN', '10.8–11.2'] },
      { id: 'rd-6', cells: ['Pb/Ag conc density', '1,260 g/L', 'OK', '1,240–1,320'] },
      { id: 'rd-7', cells: ['Paste backfill flow', '13.3 t/h', 'OK', '12–16 t/h'] },
      { id: 'rd-8', cells: ['Tailings pump flow', '185 m³/h', 'OK', '175–200 m³/h'] },
    ],
  },
  {
    roleId: 'operator',
    tabId: 'overview',
    caption: 'Key contacts — day shift',
    columns: ['Role', 'Contact'],
    rows: [
      { id: 'ct-1', cells: ['Control room', 'Ext 2200'] },
      { id: 'ct-2', cells: ['Ops supervisor', 'D. Lee · Ext 2201'] },
      { id: 'ct-3', cells: ['Metallurgist', 'S. Park · Ext 2220'] },
      { id: 'ct-4', cells: ['Maintenance', 'Ext 2230'] },
      { id: 'ct-5', cells: ['Safety officer', 'Ext 2240'] },
      { id: 'ct-6', cells: ['Plant manager', 'Ext 2100'] },
    ],
  },
  {
    roleId: 'gm',
    tabId: 'weekly',
    caption: 'Weekly summary · design-basis',
    columns: ['Metric', 'Design (PFS)', 'Actual (WTD)'],
    rows: [
      { id: 'gm-w1', cells: ['Tonnes processed', '12,600 st (7 × 1,800 stpd)', 'pending historian'] },
      { id: 'gm-w2', cells: ['Pb recovery / grade', '88.2% / 67.0% Pb', 'pending historian'] },
      { id: 'gm-w3', cells: ['Zn recovery / grade', '85.1% / 58.0% Zn', 'pending historian'] },
      { id: 'gm-w4', cells: ['Reconciliation exceptions', '0 expected', '2 open — Pb circuit · Pb mine claim [syn]'] }, // ma.D1 / #120 fix round 1 MR-F8: the second exception is derived from MINE_CLAIMS (no Bunker Hill source) — marked on the glass, not just in this comment
      { id: 'gm-w5', cells: ['Plant constraint', 'n/a', 'Grinding — unchanged vs last week'] },
      { id: 'gm-w6', cells: ['Advisories this week', 'n/a', 'See Cases on Super / Met'] },
    ],
  },
  {
    roleId: 'metallurgist',
    tabId: 'shift-report',
    caption: 'Shift report · design-basis',
    columns: ['Section', 'Content'],
    rows: [
      { id: 'met-s1', cells: ['Feed', 'WIT-021 75 stph design · 2.40% Pb · 5.44% Zn — actual pending historian'] },
      { id: 'met-s2', cells: ['Circuit recovery / grade', 'Pb 88.2%/67.0% · Zn 85.1%/58.0% · Ag 84.2%/12.13 oz/ton vs design'] },
      { id: 'met-s3', cells: ['Reconciliation', 'Pb circuit flagged — mass balance implies 112% Pb recovery vs PFS'] }, // ma.D1 / #120: Module 2 is in the mock — trailing disclaimer dropped
      { id: 'met-s4', cells: ['Bottleneck', 'Grinding (mill + cyclones) tightest on design margin; 4 of 5 cyclones in service'] },
      { id: 'met-s5', cells: ['Open cases', 'Zn recovery — CuSO4 titration requested, pending lab · tramp iron visual on CV-001'] },
      { id: 'met-s6', cells: ['Sample pipeline', 'SA01–SA08 · SA05 LATE · SA06 MISS'] }, // SA-F25: range corrected (8 points); 'all manual, no OSA' dropped -- cadence is a session toggle now, so the caption no longer asserts a plant fact the product lets the user change
      { id: 'met-s7', cells: ['Control-loop exceptions', 'LIC4010 Manual 8.2 h — Pb rougher cell 1. See Control loops table.'] },
    ],
  },
];

/** Deck trend / meter series for the H0 viz kit (see docs/plans/deck-density-viz.md). */
export type BunkerHillTrendSeries = {
  name: string;
  points: readonly number[];
  tone: 'ok' | 'warn' | 'bad' | 'neutral' | 'info';
  area?: boolean;
};

export type BunkerHillTrendReference = {
  value: number;
  label: string;
  tone: 'ok' | 'warn' | 'bad' | 'neutral' | 'info';
};

export type BunkerHillTrendBundle = {
  categories: readonly string[];
  series: readonly BunkerHillTrendSeries[];
  references?: readonly BunkerHillTrendReference[];
};

export type BunkerHillMeterDatum = {
  id: string;
  label: string;
  value: number;
  max?: number;
  tone: 'ok' | 'warn' | 'bad' | 'neutral' | 'info';
  valueLabel?: string;
  marker?: { value: number; label: string };
};

function setpointInRangeTable() {
  const table = EXTRA_ROLE_TABLES.find(
    (row) =>
      row.roleId === 'metallurgist' &&
      row.tabId === 'samples' &&
      row.caption.includes('setpoint'),
  );
  if (!table) {
    throw new Error('Missing setpoint compliance table in EXTRA_ROLE_TABLES');
  }
  return table;
}

function toneFromSetpointStatus(status: string): BunkerHillMeterDatum['tone'] {
  if (status === 'OK') return 'ok';
  if (status === 'WARN') return 'warn';
  return 'neutral';
}

/** B: slide 6 — Zn/Pb/Ag Mon–Sun + design references; values project from the recovery series. */
export const CREW_COMPLIANCE_TREND_7D: BunkerHillTrendBundle = {
  // D25: the seven complete production days before the 08 Jun cursor, labelled
  // by date only — Jun is carried by the card caption. These are D25's complete
  // production days on the deck calendar, not the civil weekday order the old
  // "Mon 01 … Sun 07" stamps implied (the plant week runs 02–08 Jun).
  categories: ['01', '02', '03', '04', '05', '06', '07'],
  series: [
    {
      name: 'Overall compliance',
      points: [94.2, 88.1, 91.8, 90.4, 86.2, 95.1, 91.4],
      tone: 'warn',
      area: true,
    },
  ],
  references: [{ value: 95, label: 'Target 95%', tone: 'neutral' }],
};

// [syn] interpolated from slide 8 — Pb/Ag conc 0.22% → 0.32% over D1–D30; Zn ~0.06% flat.
export const ARSENIC_TREND_30D: BunkerHillTrendBundle = {
  categories: Array.from({ length: 30 }, (_, index) => `D${index + 1}`),
  series: [
    {
      name: 'Pb/Ag conc',
      points: Array.from({ length: 30 }, (_, index) =>
        Number((0.22 + ((0.32 - 0.22) * index) / 29).toFixed(3)),
      ),
      tone: 'warn',
      area: true,
    },
    {
      name: 'Zn conc',
      points: Array.from({ length: 30 }, () => 0.06),
      tone: 'ok',
    },
  ],
  references: [
    { value: arsenicContractLimit('Pb'), label: `Pb/Ag limit ${ARSENIC_CONTRACT_PB}%`, tone: 'bad' },
    { value: arsenicContractLimit('Zn'), label: `Zn limit ${ARSENIC_CONTRACT_ZN}%`, tone: 'neutral' },
  ],
};

// B: slide 3 — reagent tank levels (% full).
export const REAGENT_TANK_LEVELS: readonly BunkerHillMeterDatum[] = [
  { id: 'tank-znso4', label: 'ZnSO₄ (Pb depressant)', value: 78, max: 100, tone: 'ok', valueLabel: '78%' },
  { id: 'tank-smbs', label: 'SMBS (Pb/Py depress.)', value: 65, max: 100, tone: 'warn', valueLabel: '65%' },
  { id: 'tank-cuso4', label: 'CuSO₄ (Zn activator)', value: 88, max: 100, tone: 'ok', valueLabel: '88%' },
];

// B: slide 3 — lime / pH circuit reagent bars (% full).
export const LIME_PH_CIRCUIT_BARS: readonly BunkerHillMeterDatum[] = [
  { id: 'ph-mibc', label: 'MIBC (Frother)', value: 55, max: 100, tone: 'warn', valueLabel: '55%' },
  { id: 'ph-3418a', label: '3418A (Collector)', value: 82, max: 100, tone: 'ok', valueLabel: '82%' },
  { id: 'ph-aero', label: 'Aero 5100 (Promoter)', value: 72, max: 100, tone: 'ok', valueLabel: '72%' },
];

// B: slide 8 — arsenic distribution by product (% of As reporting).
export const AS_DISTRIBUTION_BY_PRODUCT: readonly BunkerHillMeterDatum[] = [
  { id: 'as-tails', label: 'Final tails', value: 89, max: 100, tone: 'ok', valueLabel: '89%' },
  { id: 'as-pbag', label: 'Pb/Ag conc', value: 7, max: 100, tone: 'warn', valueLabel: '7%' },
  { id: 'as-zn', label: 'Zn conc', value: 4, max: 100, tone: 'ok', valueLabel: '4%' },
];

/** B: slide 7 — setpoint %-in-range; reused from the samples setpoint table. */
export const SETPOINT_IN_RANGE: readonly BunkerHillMeterDatum[] = (() => {
  const table = setpointInRangeTable();
  return table.rows.map((row) => {
    const pct = Number.parseInt(row.cells[1], 10);
    const status = row.cells[2] ?? '';
    return {
      id: row.id,
      label: row.cells[0],
      value: pct,
      max: 100,
      marker: { value: 90, label: 'Target 90%' },
      tone: toneFromSetpointStatus(status),
      valueLabel: row.cells[1],
    };
  });
})();

export const CONTROL_LOOPS: readonly ControlLoopState[] = [
  // Word-doc §M3 §3 fields: control_mode · mode_changed_at · changed_by ·
  // duration_in_mode. Every dwell / time is [syn] — neither A nor B carries
  // loop-mode data (tim-scope ui-screens §7); LIC4010 sits above the 4 h rule
  // so the "manual for an extended period" warning (A §M3 §5) actually renders.
  {
    id: 'loop-lic4010',
    tag: 'LIC4010',
    name: 'Pb rougher cell 1 level (LIT4010 / FMEA LT-140)',
    mode: 'manual',
    duration: '8.2 h', // [syn] — tim-scope §3.5 value; was 2.4 h (also [syn]) which never exercised the > 4 h rule (V14)
    modeChangedAt: '03:20', // [syn]
    changedBy: 'cro', // [syn] — A §M3 §6.4: "an operator has it there deliberately"
    note: 'Dart valves ~100% open. Operator-held — explains high level without a hydraulic fault.',
  },
  {
    id: 'loop-lic4013',
    tag: 'LIC4013',
    name: 'Pb rougher cell 4 level',
    mode: 'local',
    duration: '2.4 h', // [syn] — tim-scope §3.5
    modeChangedAt: '09:05', // [syn]
    changedBy: 'maintenance', // A: "Local mode is used for maintenance and commissioning only"
    note: 'LCU — maintenance on the dart-valve actuator; not a case.',
  },
  {
    id: 'loop-wic1201',
    tag: 'WIC1201A',
    name: 'Ball mill feed rate',
    mode: 'auto',
    duration: '5.5 h', // [syn] — in AUTO since shift start 06:00
    modeChangedAt: '06:00', // [syn]
    changedBy: 'cro', // [syn]
    note: 'On setpoint. Hardness scenario does not put this loop in manual.',
  },
  {
    id: 'loop-aic5002',
    tag: 'AIC5002',
    name: 'Zn conditioning tank pH',
    mode: 'auto',
    duration: '5.5 h', // [syn] — in AUTO since shift start 06:00; CRO confirmed AUTO 09:40 on ZN-REC
    modeChangedAt: '06:00', // [syn]
    changedBy: 'cro', // [syn]
    note: 'Automatic. Lime SP still the Zn-recovery lever on the Met dashboard.',
  },
];

/**
 * [syn] The Zn flotation pH setpoint on AIC5002 — the one seed field a surface
 * reads for it (polish builds' Zn pH ruling). The loop ran at SP 10.8 (the
 * deck's label; the 06:42 ZN-PH-01 alarm fired against it) until the ZN-REC
 * lime step, recommended action `ra-2`, raised it to 11.0 when it was
 * implemented at 08:00: the middle of the Control Plan's `cp-zn-ph` band,
 * 10.8–11.2. The step time is the action's own `implementedAt`, never typed here.
 */
export const ZN_PH_SETPOINT = {
  loopTag: 'AIC5002',
  readingId: 'rd-kpv-zn-ph',
  controlPlanRowId: 'cp-zn-ph',
  before: 10.8,
  after: 11.0,
  stepActionId: 'ra-2',
} as const;

// Word-doc §6 Met Diagnostics table, verbatim signal patterns. Priority
// reuses the plant's P1–P4 alarm philosophy (§7); the hardness row is the
// one the ScenarioEngine walkthrough plays (§8.5). The media-depletion row
// is its deliberate contrast: identical on PSA alone, opposite on kW/tph.
export const DIAGNOSTIC_RULES: readonly DiagnosticRule[] = [
  {
    id: 'dr-media',
    signalPattern: 'JT-023 kW/tph ratio declining + PSA coarsening',
    diagnosis: 'Media depleted (ball charge worn)',
    priority: 'P1',
    responsible: 'maintenance',
    recommendedAction: 'Inspect ball charge and liners — falling kW/tph is the media signature; schedule a mill inspection window.',
    causeCategory: 'mechanical-electrical',
    subprocessId: 'sp-grind',
    when: [
      { signal: 'mill-power-ratio', condition: 'falling', label: 'JT-023 kW/tph falling' },
      { signal: 'mill-feed-rate', condition: 'stable', label: 'WIT-021 feed steady' },
    ],
    rival: 'dr-hardness',
    discriminator: {
      signal: 'grind-p80',
      compare: '>',
      threshold: 85,
      evidenceKind: 'visual',
      label: 'Mill charge inspection — ball charge depleted (FMEA fm-apex media row)',
    },
    // paul-fmea.A3 (#270): MD-001's AI detection logic is the three-way
    // split dr-media/dr-hardness encode as a shared discriminator — both
    // source from this one Met Diagnostics row (2026-09-09-paul-fmea-v8.md
    // § Delta vs the mock).
    symptomGroup: 'Pb Recovery Below Target',
    affects: ['pb-recovery', 'ag-recovery'],
    sourceCauseId: ['MD-001'],
  },
  {
    id: 'dr-hardness',
    signalPattern: 'JT-023 kW/tph ratio rising + PSA coarsening',
    diagnosis: 'Ore harder than design — more energy needed per tonne',
    priority: 'P3',
    responsible: 'metallurgist',
    recommendedAction: 'Reduce feed rate 10% until PSA recovers; advise mine planner of ore change.',
    playableScenario: 'hardness',
    causeCategory: 'feed-ore',
    subprocessId: 'sp-grind',
    when: [
      { signal: 'mill-power-ratio', condition: 'rising', label: 'JT-023 kW/tph rising' },
      { signal: 'mill-feed-rate', condition: 'stable', label: 'WIT-021 feed steady' },
    ],
    rival: 'dr-media',
    discriminator: {
      signal: 'grind-p80',
      compare: '>',
      threshold: 85,
      evidenceKind: 'lab-sample',
      label: 'SA01 P80 coarser than 85 µm',
    },
    symptomGroup: 'Pb Recovery Below Target',
    affects: ['pb-recovery', 'ag-recovery'],
    sourceCauseId: ['MD-001'],
  },
  {
    id: 'dr-grind-fail',
    signalPattern: 'Both Pb + Zn recovery declining + PSA P80 > 85–90 µm + reagents/pH correct',
    diagnosis: 'Global grind failure — the single most impactful failure mode in the plant; affects every downstream circuit equally',
    priority: 'P1',
    responsible: 'metallurgist',
    recommendedAction: 'Treat plant-wide: verify cyclone configuration and feed density, cut feed until P80 recovers.',
    causeCategory: 'metallurgical-process',
    subprocessId: 'sp-grind',
    when: [
      { signal: 'pb-recovery', condition: 'falling', label: 'Pb recovery falling' },
      { signal: 'zn-recovery', condition: 'falling', label: 'Zn recovery falling' },
      { signal: 'grind-p80', condition: 'above-band', label: 'SA01 P80 above 85 µm' },
      { signal: 'pb-rougher-ph', condition: 'stable', label: 'Pb rougher pH steady' },
    ],
    rival: 'dr-mineralogy',
    symptomGroup: 'Both Pb + Zn Recovery Declining',
    affects: ['pb-recovery', 'zn-recovery', 'ag-recovery'],
    sourceCauseId: ['MD-030'],
  },
  {
    id: 'dr-throughput',
    signalPattern: 'Feed rate (WIT-021) above design + both recoveries declining together, coincident with the rate change',
    diagnosis: 'Throughput exceedance diluting reagent/grind — distinguished from media wear by the step-change timing',
    priority: 'P2',
    responsible: 'operator',
    recommendedAction: 'Bring feed rate back to design — the coincident step-change separates this from media wear.',
    causeCategory: 'metallurgical-process',
    subprocessId: 'sp-grind',
    when: [
      { signal: 'mill-feed-rate', condition: 'above-band', label: 'WIT-021 above design' },
      { signal: 'pb-recovery', condition: 'falling', label: 'Pb recovery falling' },
      { signal: 'zn-recovery', condition: 'falling', label: 'Zn recovery falling' },
    ],
    rival: 'dr-media',
    symptomGroup: 'Both Pb + Zn Recovery Declining',
    affects: ['pb-recovery', 'zn-recovery', 'ag-recovery'],
    sourceCauseId: ['MD-034'],
  },
];

// ---------------------------------------------------------------------------
// C2 "Displays / DCS Tags Already Explored" — one NodeDisplay per display Paul
// names per process box, in the form the corpus draws for it (Module 1
// fidelity corpus §2.2–§2.4, #64). Titles verbatim. Rule for numbers: a display
// is seeded only where at least one source gives a number (design / SP / limit /
// live); series shape is [syn] where no source carries history (S18) — shaped by
// C2's own good/bad rows and the deck's live state, clamped in C1 §8.2 bands.
// Displays C2 names with NO number anywhere are listed in NODE_DISPLAYS_UNSEEDED
// so a surface can say "named by Paul, awaiting historian" instead of inventing.
// Nothing renders these until the second-pass brief lands.
// Provenance keys: A = module doc (C1) · B = deck · C2 = process-flow UI concept.

/** Day-shift clock — the product bar shows 11:30 on 08 Jun 2026 (B: slide 12). */
const SHIFT_CLOCK = ['06:00', '07:00', '08:00', '09:00', '10:00', '11:00', '11:30'] as const;

export const NODE_DISPLAYS: readonly NodeDisplay[] = [
  // Grinding circuit — C2 lists four displays.
  {
    id: 'nd-gr-feed',
    subprocessId: 'sp-grind',
    kind: 'rate-vs-design',
    title: `Fresh mill feed (daily average) vs design (${BM01_FEED_DESIGN})`,
    tagIds: ['WIT-021'],
    value: Number(formatValue(BM01_FEED)),
    design: BM01_FEED.design!,
    unit: 'stph',
    max: 120,
    provenance: 'D14: rd-kpi-ore-td / 24, the measured daily fresh-feed average; design derives from the same plant Reading. A §8.2 WIC1201A DCS 0–120 stph.',
  },
  {
    id: 'nd-gr-cyclone-feed',
    subprocessId: 'sp-grind',
    kind: 'live-vs-design',
    title: 'Cyclone feed % solids, SG, and dilution ratio (live vs design table)',
    tagIds: ['DT-203', 'FIT-203', 'PT-173'],
    rows: [
      { parameter: 'Cyclone feed solids', design: '57.5%', live: '61%', provenance: 'live B: slide 12 "61% sol."' },
      { parameter: 'Cyclone underflow solids', design: '74%', live: '—' },
      { parameter: 'Cyclone overflow solids (flotation feed)', design: '37%', live: '—' },
      { parameter: 'Cyclone feed slurry SG', design: '≈1.58', live: '—' },
      { parameter: 'Underflow SG', design: '≈1.90', live: '—' },
      { parameter: 'Overflow SG', design: '≈1.31', live: '—' },
      { parameter: 'Dilution ratio (water : solids, feed)', design: '0.74', live: '—', provenance: 'derived from 57.5% solids' },
      { parameter: 'Cyclone feed pressure', design: '80 – 140 kPa', live: '84 kPa', provenance: 'design A §8.2 PI1310; live B: slide 12' },
    ],
    provenance: 'C2 Grinding §2 design parameters; live column B: slide 12 where the deck shows a value, — otherwise (historian unconnected)',
  },
  {
    id: 'nd-gr-circ-load',
    subprocessId: 'sp-grind',
    kind: 'trend-vs-target',
    title: 'Circulating load (tph and %)',
    tagIds: [],
    unit: 'stph',
    categories: SHIFT_CLOCK,
    series: [{ name: 'Circulating load', points: [186, 185, 187, 186, 188, 191, 194] }],
    references: [{ value: 186, label: 'Design 186 stph (248% of new feed)' }],
    provenance: '[syn] shift-to-date series shaped by C2 insight "a rising trend suggests grind is coarsening" and the deck\'s live P80 82 µm vs 74 design (B: slide 12, GRIND-P80 case); reference A §8.1',
  },
  {
    id: 'nd-gr-regrind-p80',
    subprocessId: 'sp-grind',
    kind: 'trend-vs-target',
    title: 'Regrind P80 trend vs 30 µm target',
    tagIds: ['DT-605'],
    unit: 'µm',
    categories: SHIFT_CLOCK,
    series: [{ name: 'Regrind P80', points: [29, 30, 30, 31, 30, 30, 30] }],
    references: [{ value: 30, label: 'Live SP ~30 µm (band 27–33)' }],
    provenance: '[syn] flat at the live SP ~30 µm (DCS FD §8.5 process-flow concept) — S2 resolved (seed-retie #258; deck design says 38 µm, band 35–42)',
  },

  // Pb conditioning & flotation — C2 lists four; cell levels are unseeded (no number).
  {
    id: 'nd-pb-ph',
    subprocessId: 'sp-pb',
    kind: 'trend-vs-target',
    title: 'Pb Rougher pH (AT-185) trend',
    tagIds: ['AT-185'],
    unit: 'pH',
    categories: SHIFT_CLOCK,
    series: [{ name: 'Pb rougher pH', points: [8.3, 8.4, 8.4, 8.3, 8.4, 8.5, 8.4] }],
    references: [
      { value: 7.5, label: 'Target low 7.5' },
      { value: 8.5, label: 'Target high 8.5' },
    ],
    provenance: '[syn] around the deck\'s live 8.4 (B: slide 12); band A §8.2 AIC4001 target 7.5–8.5 (S3: FD loop tag ↔ C2 AT-185)',
  },
  {
    id: 'nd-pb-cleaner-circ',
    subprocessId: 'sp-pb',
    kind: 'trend-vs-target',
    title: 'Cleaner circulating load',
    tagIds: ['FIT-611'],
    unit: 'dst',
    categories: SHIFT_CLOCK,
    series: [{ name: 'Pb cleaner tail → rougher feed', points: [80, 79, 81, 80, 82, 81, 80] }],
    references: [{ value: 79.9, label: 'Design 79.9 dst' }],
    provenance: '[syn] around A §8.1 internal recycle 79.9 dst; C2 bad row "cleaner feed box recycle building" is the direction to watch',
  },
  {
    id: 'nd-pb-grade',
    subprocessId: 'sp-pb',
    kind: 'live-vs-design',
    title: 'Pb concentrate grade vs sample assays',
    tagIds: ['SA03'],
    rows: [
      { parameter: 'Pb grade (final Pb con)', design: '67.0%', live: '67%', provenance: 'S1 resolved to PFS §7.1 (seed-retie #258; deck design says 65.0%); live B: slide 12' },
      { parameter: 'Ag content', design: '12.13 oz/t', live: '12.14 oz/t', provenance: 'S1 resolved to PFS §8.4 (seed-retie #258; deck design says 32.0 oz/t); live K20 (#717) balance-implied from head × recovery ÷ mass pull' },
    ],
    provenance: 'A §8.4 SA03 spec — S1 resolved to PFS design row (seed-retie #258; deck design column superseded); live values B: slide 12',
  },

  // Zn conditioning & flotation — C2 lists three; cell levels are unseeded.
  {
    id: 'nd-zn-ph',
    subprocessId: 'sp-zn',
    kind: 'trend-vs-target',
    title: 'Zn Rougher pH (AT-150) and Zn 1st Cleaner pH (AT-176)',
    tagIds: ['AT-150', 'AT-176'],
    unit: 'pH',
    categories: SHIFT_CLOCK,
    series: [
      { name: 'Zn rougher pH (AT-150)', points: [10.9, 10.8, 10.8, 10.6, 10.5, 10.3, 10.2] },
      { name: 'Zn 1st cleaner pH (AT-176)', points: [10.7, 10.7, 10.6, 10.7, 10.6, 10.6, 10.6] },
    ],
    references: [
      { value: 10.8, label: 'Rougher SP 10.8 (deck)' },
      { value: 11.0, label: 'Conditioner target low 11.0 (FD AIC5002)' },
    ],
    provenance: '[syn] AT-150 declining to the deck\'s live 10.2 (B: slide 12 alarm ZN-pH-01 "LOW 10.2, SP 10.8" — S7); AT-176 flat; both bands seeded per S14 (different vessels)',
  },
  {
    id: 'nd-zn-grade',
    subprocessId: 'sp-zn',
    kind: 'live-vs-design',
    title: 'Zn concentrate grade vs sample assays',
    tagIds: ['SA04'],
    rows: [
      { parameter: 'Zn grade (final Zn con)', design: '58.0%', live: '58.4%', provenance: 'S1 resolved to PFS §7.1 (seed-retie #258; deck design says 52.0%); live B: slide 12' },
      { parameter: 'Pb in Zn con', design: '< 3%', live: '—', provenance: 'A §8.4' },
    ],
    provenance: 'A §8.4 SA04 spec — S1 resolved to PFS design row (seed-retie #258; deck design column superseded); live B: slide 12',
  },

  // Concentrate thickening & filtration — C2 lists three; filter density / vacuum unseeded.
  {
    id: 'nd-fl-rake',
    subprocessId: 'sp-filter',
    kind: 'trend-vs-target',
    title: 'Rake torque/height trends (Pb and Zn)',
    tagIds: ['OT-200', 'OT-250', 'ZT-201', 'ZT-251'],
    unit: '% torque',
    categories: SHIFT_CLOCK,
    series: [
      { name: 'Pb thickener rake torque (OT-200)', points: [41, 42, 42, 43, 42, 43, 44] },
      { name: 'Zn thickener rake torque (OT-250)', points: [47, 48, 48, 49, 50, 50, 51] },
    ],
    references: [{ value: 70, label: 'High alarm 70%' }],
    provenance: '[syn] both inside band; alarm A §8.2 thickener rake torque high 70%; C2 bad row "torque climbing — bed building" is the direction to watch',
  },
  {
    id: 'nd-fl-pb-rate',
    subprocessId: 'sp-filter',
    kind: 'rate-vs-design',
    title: 'Concentrate production rate (loadout weighers)',
    tagIds: ['WT-301'],
    value: 52.4,
    design: 56.9,
    unit: 'dst/d Pb/Ag con',
    max: 100,
    provenance: 'design A §8.1 Pb/Ag concentrate 56.9 dst/d — S19 resolved (seed-retie #258); live recomputed from the mass balance (feed 1,720 t/d × head 2.40% Pb × rec 85.2% ÷ grade 67.1% = 52.4 dst/d, −7.9% vs design); deck design 28 t/shift stays on provenance (per-shift figure, no daily-rate equivalent given in the deck); scale [syn]',
  },
  {
    id: 'nd-fl-zn-rate',
    subprocessId: 'sp-filter',
    kind: 'rate-vs-design',
    title: 'Concentrate production rate (loadout weighers)',
    tagIds: ['WT-272', 'ST-202'],
    value: 131.9,
    design: 143.7,
    unit: 'dst/d Zn con',
    max: 200,
    provenance: 'design A §8.1 Zn concentrate 143.7 dst/d — S19 resolved (seed-retie #258); live recomputed from the mass balance (feed 1,720 t/d × head 5.44% Zn × rec 82.3% ÷ grade 58.4% = 131.9 dst/d, −8.2% vs design); deck design 82 t/shift stays on provenance (per-shift figure, no daily-rate equivalent given in the deck); scale [syn]',
  },

  // Process water & gland water — C2 lists two; tank level unseeded.
  {
    id: 'nd-wa-fit022',
    subprocessId: 'sp-water',
    kind: 'rate-vs-design',
    title: 'FIT-022 mill water addition flow vs ~300 gpm design',
    tagIds: ['FIT-022'],
    value: 300,
    design: 300,
    unit: 'gpm',
    max: 400,
    provenance: 'C2 Grinding §2 "~300 gpm (≈68.1 m³/hr)"; value = design basis while the historian is unconnected; scale [syn]',
  },
];

/** C2 names these displays but no source carries a number for them — listed, not invented. */
export const NODE_DISPLAYS_UNSEEDED: readonly { subprocessId: string; title: string; tagIds: readonly string[] }[] = [
  { subprocessId: 'sp-pb', title: 'Cell levels LT-140 / LT-141', tagIds: ['LT-140', 'LT-141'] },
  { subprocessId: 'sp-zn', title: 'Cell levels LT-125/160/161/181/182', tagIds: ['LT-125', 'LT-160', 'LT-161', 'LT-181', 'LT-182'] },
  { subprocessId: 'sp-filter', title: 'Filter feed density and vacuum pressure', tagIds: ['DT-252', 'DT-253', 'PIT-210'] },
  { subprocessId: 'sp-paste', title: 'Tails Pump Box and Filter Feed Tank levels', tagIds: [] },
  { subprocessId: 'sp-paste', title: 'Thickener overflow clarity / turbidity (if instrumented)', tagIds: [] },
  { subprocessId: 'sp-water', title: 'Process water tank level', tagIds: [] },
];
// C2 has no crushing box — sp-crush has no displays by design (the fence, not a gap).

// ---------------------------------------------------------------------------
// Slide 12 flow strip — the CRO's twelve unit-op cards in two connected rows,
// values verbatim from the deck (B: slide 12), one row per card in slide order.
// Deck live values on a deck-derived screen (S5); /plant-model keeps A's bands.
// Tone is DERIVED in FlowStrip.transform.ts: `watch` (the slide's orange border on
// Paste) → warning; `flaggedByAlarm` joins ALARMS by code and takes the alarm's
// priority tone — only where the alarm flags the value the card shows (ZN-PH-01
// on Zn rougher pH; AG-CONC on Pb/Ag CLN Ag grade). BM-BRG flags bearing temp,
// not Ball mill's shown value → no tone. Circuit is grouping (the slide's teal /
// blue borders), never a state token. Consumed by cut V6 of the Module 1 brief.
export type FlowStripCircuit = 'grind-pb' | 'zn-dewater';

export type FlowStripCell = {
  id: string;
  /** Slide caption, as written (caps on the glass are a style, not data). */
  label: string;
  headline: string;
  sub: string;
  row: 1 | 2;
  circuit: FlowStripCircuit;
  /** UnitOperation id when the card is a seeded unit; stream cards (Pb tails → Zn feed) have none. */
  unitId?: string;
  /** Alarm code whose message flags this card's shown value (join to ALARMS). */
  flaggedByAlarm?: string;
  /** The slide's orange border on Paste — a watch state, not a circuit colour. */
  watch?: boolean;
  provenance: string;
};

export const FLOW_STRIP_CELLS: readonly FlowStripCell[] = [
  { id: 'fs-crusher', label: 'Surface crusher', headline: '680 t/h', sub: 'CSS 110mm', row: 1, circuit: 'grind-pb', unitId: 'CR01', provenance: 'B: slide 12 (D7: 680 t/h vs 864 t/d crushed on slide 3 — deck-internal)' },
  { id: 'fs-ball-mill', label: 'Ball mill', headline: BM01_FEED_LABEL, sub: `Design ${BM01_FEED_DESIGN}`, row: 1, circuit: 'grind-pb', unitId: 'BM01', provenance: BM01_FEED.provenance },
  { id: 'fs-cyclones', label: 'Cyclones', headline: '61% sol.', sub: 'Cut: 82µm', row: 1, circuit: 'grind-pb', unitId: 'CY01', provenance: 'B: slide 12' },
  { id: 'fs-regrind', label: 'Regrind', headline: '18 t/h', sub: 'P80=30µm', row: 1, circuit: 'grind-pb', unitId: 'RG01', provenance: 'DCS FD §8.5 process-flow concept ~30 µm — S2 resolved (seed-retie #258; deck design says 38 µm)' },
  { id: 'fs-pb-rougher', label: 'Pb rougher', headline: 'pH 8.4', sub: 'Air:95 m³/h', row: 1, circuit: 'grind-pb', unitId: 'PB01', provenance: 'B: slide 12' },
  { id: 'fs-pb-cln', label: 'Pb/Ag CLN', headline: 'Grade 67%', sub: 'Ag 12.14 oz/t', row: 1, circuit: 'grind-pb', flaggedByAlarm: 'AG-CONC', provenance: 'K20 (#717) — live Pb/Ag conc grade tracks rd-kpi-pb-grade / rd-kpi-ag-grade (S1: A spec 67.0% Pb / 12.13 oz/t design; balance-implied Ag 12.14 oz/t)' },
  { id: 'fs-pb-tails', label: 'Pb tails → Zn feed', headline: '6.1% Zn', sub: 'ZnSO4+SMBS cond.', row: 2, circuit: 'zn-dewater', provenance: 'B: slide 12 (stream card — SA07 edge)' },
  { id: 'fs-zn-rougher', label: 'Zn rougher', headline: 'pH 10.8', sub: 'CuSO4 act.', row: 2, circuit: 'zn-dewater', unitId: 'ZN01', flaggedByAlarm: 'ZN-PH-01', provenance: 'B: slide 12 (S7: 10.8 is the setpoint label; live 10.2 per the ZN-PH-01 alarm on the same slide)' },
  { id: 'fs-zn-cleaner', label: 'Zn cleaner', headline: 'Grade 58%', sub: 'Rec: 82.3%', row: 2, circuit: 'zn-dewater', provenance: 'B: slide 12 — live Zn conc grade tracks rd-kpi-zn-grade (58.4%, design 58.0% A PFS §7.1; deck design says 52%)' },
  { id: 'fs-zn-thickener', label: 'Zn thickener', headline: '58% UF', sub: 'OF: clear', row: 2, circuit: 'zn-dewater', unitId: 'TK-ZN', provenance: 'B: slide 12' },
  { id: 'fs-zn-filter', label: 'Zn filter', headline: 'Moist 11.2%', sub: '—', row: 2, circuit: 'zn-dewater', unitId: 'FL-ZN', provenance: 'B: slide 12' },
  { id: 'fs-paste', label: 'Paste', headline: '318 t/d', sub: 'Backfill', row: 2, circuit: 'zn-dewater', unitId: 'PS01', watch: true, provenance: 'B: slide 12 (orange border on the slide = watch)' },
];

/** Layer-A constants for the daily management report — projected from seed, not C3 literals. */
export const REPORT_DAILY = {
  /** [syn] — pro-rata over phaseMonth days (08 Jun = day 8 of June). */
  mtdDaysInPhaseMonth: 8,
  /** sup-r5 plant-wide row — 24h actual vs night-shift band reference (96.2). */
  millAvailabilityPct: { actual: 96.0, target: 96.2 },
} as const;

// Module 4 template registry — Paul's C3 daily email + in-app weekly / shift (G2).
export const REPORT_TEMPLATES: readonly ReportTemplate[] = [
  {
    id: 'tpl-daily-management',
    title: 'Daily management report',
    audienceRole: ['GM', 'Ops Manager', 'Corporate Metallurgy', 'Finance'],
    granularity: 'daily',
    detailLevel: 'executive',
    comparisonBasis: 'budget',
    channel: 'email',
    cadence: '08:00 daily',
    deliveredTo: ['GM', 'Ops Manager', 'Corporate Metallurgy', 'Finance'],
    workflowId: 'reports.daily-management',
    nextSend: '09 Jun 08:00', // [syn]
  },
  {
    id: 'tpl-daily-accounting',
    title: 'Daily accounting report',
    audienceRole: ['Metallurgist', 'GM'],
    granularity: 'daily',
    detailLevel: 'operational',
    comparisonBasis: 'design',
    channel: 'email',
    cadence: '08:00 daily',
    deliveredTo: ['Metallurgist', 'GM'],
    workflowId: 'reports.daily-accounting',
    nextSend: '09 Jun 08:00', // [syn]
  },
  {
    id: 'tpl-weekly-summary',
    title: 'Weekly summary',
    audienceRole: ['GM'],
    granularity: 'weekly',
    detailLevel: 'executive',
    comparisonBasis: 'budget',
    channel: 'in-app',
    cadence: 'Weekly',
    deliveredTo: ['GM'],
    workflowId: 'reports.weekly-summary',
    nextSend: 'Mon 15 Jun 06:00', // [syn]
  },
  {
    id: 'tpl-met-shift',
    title: 'Met shift report',
    audienceRole: ['Metallurgist'],
    granularity: 'shift',
    detailLevel: 'technical',
    comparisonBasis: 'budget',
    channel: 'in-app',
    cadence: 'Per shift',
    deliveredTo: ['Metallurgist'],
    workflowId: 'reports.met-shift',
    nextSend: 'Shift end', // [syn]
  },
];

/** Frozen at plant-model v2 · rule-set v2 (matches MODEL_VERSIONS / RULE_SET_VERSIONS latest n). */
function reportEvaluatedAgainst(cursor: typeof DEFAULT_CURSOR) {
  return { modelVersion: 2, ruleSetVersion: 2, cursor };
}

export const REPORT_INSTANCES: readonly ReportInstance[] = [
  {
    templateId: 'tpl-daily-management',
    period: '08 Jun 2026',
    generatedAt: '08 Jun 08:00',
    deliveredAt: '08 Jun 08:02', // [syn]
    evaluatedAgainst: reportEvaluatedAgainst('2026-06-08T08:00'),
  },
  {
    templateId: 'tpl-daily-accounting',
    period: '08 Jun 2026',
    generatedAt: '08 Jun 08:00',
    deliveredAt: '08 Jun 08:02', // [syn]
    evaluatedAgainst: reportEvaluatedAgainst('2026-06-08T08:00'),
  },
  {
    templateId: 'tpl-weekly-summary',
    period: 'Week to 08 Jun 2026',
    generatedAt: '08 Jun 06:00',
    evaluatedAgainst: reportEvaluatedAgainst('2026-06-08T06:00'),
  },
  {
    templateId: 'tpl-met-shift',
    period: formatClock(DEFAULT_CURSOR),
    generatedAt: '08 Jun 11:30',
    evaluatedAgainst: reportEvaluatedAgainst(DEFAULT_CURSOR),
  },
];
